/* eslint-disable */
/*
 * mp4-untrunc.js — Untrunc-style MP4 sample-table reconstruction.
 *
 * Recovers Android/iPhone interrupted recordings where the moov atom was
 * never written (battery/storage/swipe-out kill). Inspired by Federico Ponchio's
 * Untrunc (https://github.com/ponchio/untrunc); reimplemented in JS for in-browser use.
 *
 * Pipeline:
 *   1. Parse the working REFERENCE file's moov (mvhd/trak/mdia/.../stbl).
 *   2. Locate the BROKEN file's mdat (even when ftyp/free preceding it is corrupt).
 *   3. Scan broken's mdat sample-by-sample, distinguishing video NAL units
 *      from AAC ADTS/raw frames using the codec template from the reference.
 *   4. Synthesise a fresh moov: stsz from scan, stco/co64 from scan positions,
 *      stsc/stts/stss/stsd cloned from the reference.
 *   5. Output a valid MP4: ftyp(ref) + new moov + broken's mdat. Plays directly,
 *      no FFmpeg pass required for the recovery itself — only for optional re-mux.
 *
 * Recovery yield on Android moov-missing files (with reference):
 *   - Both video and audio recovered (first path that recovers audio).
 *   - Truncated tail handled by clipping sample tables to whatever bytes survived.
 *
 * Public API:
 *   window.Mp4Untrunc.reconstruct(brokenFile, referenceFile, opts) → Blob | null
 *   window.Mp4Untrunc.canReconstruct(referenceFile) → Promise<boolean>
 */
(function (global) {
    'use strict';

    var FOURCC_MOOV = 0x6d6f6f76; // 'moov'
    var FOURCC_MDAT = 0x6d646174; // 'mdat'
    var FOURCC_FTYP = 0x66747970; // 'ftyp'
    var FOURCC_TRAK = 0x7472616b;
    var FOURCC_MDIA = 0x6d646961;
    var FOURCC_MINF = 0x6d696e66;
    var FOURCC_STBL = 0x7374626c;
    var FOURCC_STSD = 0x73747364;
    var FOURCC_STSZ = 0x7374737a;
    var FOURCC_STSC = 0x73747363;
    var FOURCC_STCO = 0x7374636f;
    var FOURCC_CO64 = 0x636f3634;
    var FOURCC_STTS = 0x73747473;
    var FOURCC_STSS = 0x73747373;
    var FOURCC_HDLR = 0x68646c72;
    var FOURCC_MVHD = 0x6d766864;
    var FOURCC_TKHD = 0x746b6864;
    var FOURCC_MDHD = 0x6d646864;
    var FOURCC_EDTS = 0x65647473;

    function fourcc(buf, pos) {
        return ((buf[pos] << 24) | (buf[pos + 1] << 16) | (buf[pos + 2] << 8) | buf[pos + 3]) >>> 0;
    }

    function readU32(buf, pos) {
        return ((buf[pos] << 24) | (buf[pos + 1] << 16) | (buf[pos + 2] << 8) | buf[pos + 3]) >>> 0;
    }

    function readU64(buf, pos) {
        var hi = readU32(buf, pos);
        var lo = readU32(buf, pos + 4);
        // Safe up to 2^53; MP4 boxes never realistically exceed that.
        return hi * 4294967296 + lo;
    }

    function writeU32(buf, pos, val) {
        buf[pos]     = (val >>> 24) & 0xff;
        buf[pos + 1] = (val >>> 16) & 0xff;
        buf[pos + 2] = (val >>> 8)  & 0xff;
        buf[pos + 3] = val & 0xff;
    }

    function writeU64(buf, pos, val) {
        var hi = Math.floor(val / 4294967296);
        var lo = val >>> 0;
        writeU32(buf, pos, hi);
        writeU32(buf, pos + 4, lo);
    }

    function fourccString(code) {
        return String.fromCharCode((code >>> 24) & 0xff, (code >>> 16) & 0xff, (code >>> 8) & 0xff, code & 0xff);
    }

    /**
     * Iterate top-level boxes in a Uint8Array. Yields {type, headerSize, dataOffset, size, end}.
     * Stops on size=0 (extends to end) or invalid sizes.
     */
    function eachTopBox(buf, start, end, cb) {
        var pos = start;
        while (pos + 8 <= end) {
            var size = readU32(buf, pos);
            var type = fourcc(buf, pos + 4);
            var hdr = 8;
            if (size === 1) {
                if (pos + 16 > end) break;
                size = readU64(buf, pos + 8);
                hdr = 16;
            } else if (size === 0) {
                size = end - pos;
            }
            if (size < hdr || pos + size > end) break;
            var stop = cb({ type: type, headerSize: hdr, dataOffset: pos + hdr, size: size, offset: pos, end: pos + size });
            if (stop === true) return;
            pos += size;
        }
    }

    /**
     * Find a single child box by type inside [start, end). Returns descriptor or null.
     */
    function findBox(buf, type, start, end) {
        var found = null;
        eachTopBox(buf, start, end, function (b) {
            if (b.type === type) { found = b; return true; }
        });
        return found;
    }

    /**
     * Find a box by path: ['moov', 'trak', 'mdia', 'minf', 'stbl', 'stsd'].
     * Honours sample-entry header skips for stsd/avc1/hvc1.
     */
    function findPath(buf, path, start, end) {
        var current = { dataOffset: start, end: end };
        for (var i = 0; i < path.length; i++) {
            var typeStr = path[i];
            var typeCode = ((typeStr.charCodeAt(0) << 24) | (typeStr.charCodeAt(1) << 16) | (typeStr.charCodeAt(2) << 8) | typeStr.charCodeAt(3)) >>> 0;
            var b = findBox(buf, typeCode, current.dataOffset, current.end);
            if (!b) return null;
            current = b;
            // 'stsd' has 8-byte version+flags+entry_count before child entries.
            if (typeStr === 'stsd') current.dataOffset += 8;
            // Sample-entry boxes have 78-byte header before child boxes.
            if (typeStr === 'avc1' || typeStr === 'avc3' || typeStr === 'hvc1' || typeStr === 'hev1' || typeStr === 'mp4a') {
                current.dataOffset += 78;
                if (typeStr === 'mp4a') current.dataOffset = b.dataOffset + 28; // mp4a's child boxes start at +28
            }
        }
        return current;
    }

    /**
     * Locate mdat in a broken file. Returns {start, size} or null.
     * Tries box-walk first, then byte-scan for ASCII 'mdat'.
     */
    async function findMdat(file) {
        // Header-only top-level box walk — 16 bytes read per box, then JUMP by the
        // declared size, so mdat is found at ANY offset for O(#boxes) cost. The old
        // "read first 1 MB and walk inside it" lost to recorders that reserve moov
        // space up-front ([ftyp][free ...MB][mdat] — Zoom's converter, CCTV writers;
        // the reserve grows with recording length, so no fixed window survives a
        // long enough recording — Eugenie's 174 MB Zoom conference, 2026-07-26).
        var pos = 0, hops = 0;
        while (pos + 8 <= file.size && hops++ < 512) {
            var hdr = new Uint8Array(await file.slice(pos, Math.min(pos + 16, file.size)).arrayBuffer());
            if (hdr.length < 8) break;
            var bsz = readU32(hdr, 0);
            var btype = fourcc(hdr, 4);
            var bhdr = 8;
            if (bsz === 1 && hdr.length >= 16) {
                bsz = readU32(hdr, 8) * 4294967296 + readU32(hdr, 12);
                bhdr = 16;
            } else if (bsz === 0) {
                bsz = file.size - pos;
            }
            if (btype === FOURCC_MDAT) return { start: pos + bhdr, size: file.size - (pos + bhdr) };
            if (bsz < 8) break; // corrupt size — box chain unusable, fall to byte-scan
            pos += bsz;
        }

        // Byte scan for the 'mdat' box header, covering a corrupt/garbage prefix that
        // makes the box chain unusable from byte 0.
        //
        // Scans the WHOLE file, not a fixed head window. The old 32 MB limit was the
        // single largest bail on the standalone path — `no_mdat`, 333 sessions / 30 d,
        // averaging 218 MB per file — and it is a limit of the SCAN, not a property of
        // the file: a recording whose box chain is broken (undelete/carve output, where
        // the extent begins inside another file) keeps its mdat wherever the recorder
        // put it, routinely past 32 MB. Reproduced on a 58.7 MB fixture with mdat at
        // ~42 MB: bailed `no_mdat` before, found now.
        //
        // Cost is a sequential disk read in 4 MB slices — no heap growth, since each
        // slice is released before the next. That is the same I/O the repair itself
        // does moments later, so it buys the file back for a fraction of the work
        // already committed.
        var CHUNK = 4 * 1048576;
        for (var base = 0; base < file.size; base += CHUNK - 8) {
            var end = Math.min(base + CHUNK, file.size);
            var head = new Uint8Array(await file.slice(base, end).arrayBuffer());
            for (var i = 0; i + 8 <= head.length; i++) {
                if (head[i] !== 0x6d || head[i + 1] !== 0x64 || head[i + 2] !== 0x61 || head[i + 3] !== 0x74) continue;
                // 'mdat' also occurs inside compressed video data. A real box header has
                // the 4-byte size IN FRONT of the type, so validate it before trusting
                // the hit — over a whole 700 MB file an unvalidated match is a coin toss.
                var abs = base + i;
                // Need the 4 size bytes in front of the type. Chunks overlap by 8, so a
                // match this close to a chunk start was already inspected — with its
                // preceding bytes present — at the tail of the previous chunk.
                if (i < 4) continue;
                var szOff = i - 4;
                var declared = (head[szOff] << 24 | head[szOff + 1] << 16 | head[szOff + 2] << 8 | head[szOff + 3]) >>> 0;
                var dataStart = abs + 4;
                // Accept: 0 = "extends to EOF" (a recorder killed before finalising, which
                // is exactly this cohort), 1 = 64-bit size follows, or a size that fits.
                if (declared === 0 || declared === 1 ||
                    (declared >= 8 && (abs - 4) + declared <= file.size)) {
                    if (declared === 1) dataStart = abs + 12;
                    return { start: dataStart, size: file.size - dataStart };
                }
            }
        }
        return null;
    }

    /*
     * No mdat box at all — the whole file is the body (28.09.2026).
     *
     * The head of the file is gone: a carved or cut-off extent (one report was exactly
     * 128 MiB), a junk prefix, or an mdat header declaring more than the truncated file
     * holds (findMdat rightly refuses that one — inside compressed data the fourcc alone
     * is a coin toss). The body is still an AVCC chain, and scanMdat finds its pictures
     * by itself: the rules that pick a picture START out of noise are the ones that skip
     * the audio between chunks. RU, 30 days: 21 sessions bailed `no_mdat`, in 10 of them
     * MediaLocate had found the chain (from 97 bytes to 616 MB in) or an mdat the header
     * walk could not reach. A file that is not AVCC yields no plausible chain and bails
     * later (`no_nals`); any output still goes through the caller's decode check.
     * `via` says how the body was found: 'box' (header walk / byte scan) or 'whole'.
     */
    async function findMdatOrWhole(file) {
        var m = await findMdat(file);
        if (m) { m.via = 'box'; return m; }
        return { start: 0, size: file.size, via: 'whole' };
    }

    /**
     * Read an entire file (or as much as needed) as Uint8Array.
     */
    async function readFile(file, max) {
        var size = max ? Math.min(file.size, max) : file.size;
        return new Uint8Array(await file.slice(0, size).arrayBuffer());
    }

    /**
     * Extract codec params from a sample entry inside stsd.
     * Returns {kind:'video'|'audio', codec, width, height, nalLengthSize, sps, pps, vps,
     *          channels, sampleRate, samplesPerFrame}
     */
    function parseSampleEntry(buf, stsdInfo) {
        if (!stsdInfo) return null;
        // First entry sits at stsdInfo.dataOffset (already +8'd).
        var p = stsdInfo.dataOffset;
        var end = stsdInfo.end;
        if (p + 8 > end) return null;
        var entrySize = readU32(buf, p);
        var entryType = fourcc(buf, p + 4);
        var entryEnd = Math.min(p + entrySize, end);
        var typeStr = fourccString(entryType);

        if (typeStr === 'avc1' || typeStr === 'avc3') {
            // VisualSampleEntry layout: 6 reserved + 2 dataRefIdx + 16 pre-defined + 2 width + 2 height + 8 + 4 ...
            // width@p+24, height@p+26 (after 6+2+16=24 bytes from data start of entry header).
            // Actually entry data starts after 8-byte box header. Then:
            //   8: SampleEntry (6 reserved + 2 dataRefIdx)
            //   followed by VisualSampleEntry (16 + 2 + 2 + 4 + 4 + 4 + 2 + 32 + 2 + 2 + 4 = 70 bytes)
            // Width = at offset 24 from box data (after 8 header) = at entry_pos+8+24=p+32
            var width = (buf[p + 32] << 8) | buf[p + 33];
            var height = (buf[p + 34] << 8) | buf[p + 35];
            // avcC sits as a child box after the 78-byte VisualSampleEntry header.
            var avcC = findBox(buf, 0x61766343, p + 8 + 78, entryEnd); // 'avcC'
            var nalLengthSize = 4, sps = [], pps = [];
            if (avcC) {
                var ap = avcC.dataOffset;
                if (avcC.size >= 7 + 8 && buf[ap] === 1) {
                    nalLengthSize = (buf[ap + 4] & 0x03) + 1;
                    var nSps = buf[ap + 5] & 0x1f;
                    var ip = ap + 6;
                    for (var s = 0; s < nSps && ip + 2 <= avcC.end; s++) {
                        var slen = (buf[ip] << 8) | buf[ip + 1];
                        ip += 2;
                        if (ip + slen > avcC.end) break;
                        sps.push(buf.slice(ip, ip + slen));
                        ip += slen;
                    }
                    if (ip < avcC.end) {
                        var nPps = buf[ip++];
                        for (var pp = 0; pp < nPps && ip + 2 <= avcC.end; pp++) {
                            var plen = (buf[ip] << 8) | buf[ip + 1];
                            ip += 2;
                            if (ip + plen > avcC.end) break;
                            pps.push(buf.slice(ip, ip + plen));
                            ip += plen;
                        }
                    }
                }
            }
            return { kind: 'video', codec: 'h264', width: width, height: height, nalLengthSize: nalLengthSize, sps: sps, pps: pps };
        }

        if (typeStr === 'hvc1' || typeStr === 'hev1') {
            var widthH = (buf[p + 32] << 8) | buf[p + 33];
            var heightH = (buf[p + 34] << 8) | buf[p + 35];
            var hvcC = findBox(buf, 0x68766343, p + 8 + 78, entryEnd); // 'hvcC'
            var hNalLen = 4, hSps = [], hPps = [], hVps = [];
            if (hvcC && hvcC.size >= 23 + 8) {
                var hp = hvcC.dataOffset + 22;
                hNalLen = (buf[hvcC.dataOffset + 21] & 0x03) + 1;
                var nArr = buf[hp++];
                for (var a = 0; a < nArr && hp + 3 <= hvcC.end; a++) {
                    var nalType = buf[hp] & 0x3f;
                    var numNal = (buf[hp + 1] << 8) | buf[hp + 2];
                    hp += 3;
                    for (var nn = 0; nn < numNal && hp + 2 <= hvcC.end; nn++) {
                        var nlen = (buf[hp] << 8) | buf[hp + 1];
                        hp += 2;
                        if (hp + nlen > hvcC.end) break;
                        var nu = buf.slice(hp, hp + nlen);
                        if (nalType === 32) hVps.push(nu);
                        else if (nalType === 33) hSps.push(nu);
                        else if (nalType === 34) hPps.push(nu);
                        hp += nlen;
                    }
                }
            }
            return { kind: 'video', codec: 'hevc', width: widthH, height: heightH, nalLengthSize: hNalLen, sps: hSps, pps: hPps, vps: hVps };
        }

        if (typeStr === 'mp4a') {
            // AudioSampleEntry: 8 reserved + 2 channels + 2 sampleSize + 4 reserved + 4 sampleRate(<<16)
            // Box data layout from p+8: 8 reserved (6+2 dataRefIdx) + AudioSampleEntry data.
            var channels = (buf[p + 8 + 16] << 8) | buf[p + 8 + 17];
            var sampleRate = ((buf[p + 8 + 24] << 8) | buf[p + 8 + 25]); // upper 16 bits of fixed-point sample rate
            return { kind: 'audio', codec: 'aac', channels: channels, sampleRate: sampleRate, samplesPerFrame: 1024 };
        }

        return null;
    }

    /**
     * Parse a stsz (sample sizes) box. Returns {sampleSize, sampleCount, sizes:[]}.
     * If sampleSize > 0, all samples are that size and sizes[] is empty.
     */
    function parseStsz(buf, info) {
        if (!info || info.size < 20) return null;
        var p = info.dataOffset + 4; // skip version + flags
        var sampleSize = readU32(buf, p);
        var count = readU32(buf, p + 4);
        var sizes = [];
        if (sampleSize === 0) {
            var ip = p + 8;
            for (var i = 0; i < count && ip + 4 <= info.end; i++) {
                sizes.push(readU32(buf, ip));
                ip += 4;
            }
        }
        return { sampleSize: sampleSize, sampleCount: count, sizes: sizes };
    }

    /**
     * Parse a stsc (sample-to-chunk) box. Returns array of {firstChunk, samplesPerChunk, sampleDescIndex}.
     */
    function parseStsc(buf, info) {
        if (!info || info.size < 16) return null;
        var p = info.dataOffset + 4;
        var entryCount = readU32(buf, p);
        var entries = [];
        var ip = p + 4;
        for (var i = 0; i < entryCount && ip + 12 <= info.end; i++) {
            entries.push({
                firstChunk: readU32(buf, ip),
                samplesPerChunk: readU32(buf, ip + 4),
                sampleDescIndex: readU32(buf, ip + 8)
            });
            ip += 12;
        }
        return entries;
    }

    /**
     * Parse stco (32-bit) or co64 (64-bit) chunk offsets.
     */
    function parseStco(buf, info, is64) {
        if (!info || info.size < 16) return null;
        var p = info.dataOffset + 4;
        var entryCount = readU32(buf, p);
        var offsets = [];
        var ip = p + 4;
        var step = is64 ? 8 : 4;
        for (var i = 0; i < entryCount && ip + step <= info.end; i++) {
            offsets.push(is64 ? readU64(buf, ip) : readU32(buf, ip));
            ip += step;
        }
        return offsets;
    }

    /**
     * Parse stts (time-to-sample): array of {sampleCount, sampleDelta}.
     */
    function parseStts(buf, info) {
        if (!info || info.size < 16) return null;
        var p = info.dataOffset + 4;
        var entryCount = readU32(buf, p);
        var entries = [];
        var ip = p + 4;
        for (var i = 0; i < entryCount && ip + 8 <= info.end; i++) {
            entries.push({ sampleCount: readU32(buf, ip), sampleDelta: readU32(buf, ip + 4) });
            ip += 8;
        }
        return entries;
    }

    /**
     * Parse mdhd to get track timescale + duration.
     */
    function parseMdhd(buf, info) {
        if (!info || info.size < 12) return null;
        var version = buf[info.dataOffset];
        var p = info.dataOffset + 4;
        if (version === 1) {
            return { timescale: readU32(buf, p + 16), duration: readU64(buf, p + 20) };
        }
        return { timescale: readU32(buf, p + 8), duration: readU32(buf, p + 12) };
    }

    /**
     * Parse hdlr to get handler type ('vide', 'soun', etc).
     */
    function parseHdlr(buf, info) {
        if (!info || info.size < 24) return null;
        var p = info.dataOffset + 4 + 4; // skip version+flags + pre_defined
        return fourccString(fourcc(buf, p));
    }

    /**
     * Parse a single trak. Returns track descriptor with all sample tables and codec info.
     */
    function parseTrak(buf, trakInfo) {
        var mdia = findBox(buf, FOURCC_MDIA, trakInfo.dataOffset, trakInfo.end);
        if (!mdia) return null;
        var hdlrInfo = findBox(buf, FOURCC_HDLR, mdia.dataOffset, mdia.end);
        var mdhdInfo = findBox(buf, FOURCC_MDHD, mdia.dataOffset, mdia.end);
        var minf = findBox(buf, FOURCC_MINF, mdia.dataOffset, mdia.end);
        if (!minf) return null;
        var stbl = findBox(buf, FOURCC_STBL, minf.dataOffset, minf.end);
        if (!stbl) return null;
        var stsdInfo = findBox(buf, FOURCC_STSD, stbl.dataOffset, stbl.end);
        if (stsdInfo) stsdInfo.dataOffset += 8; // skip version+flags+entry_count
        var stszInfo = findBox(buf, FOURCC_STSZ, stbl.dataOffset, stbl.end);
        var stscInfo = findBox(buf, FOURCC_STSC, stbl.dataOffset, stbl.end);
        var stcoInfo = findBox(buf, FOURCC_STCO, stbl.dataOffset, stbl.end);
        var co64Info = findBox(buf, FOURCC_CO64, stbl.dataOffset, stbl.end);
        var sttsInfo = findBox(buf, FOURCC_STTS, stbl.dataOffset, stbl.end);
        var stssInfo = findBox(buf, FOURCC_STSS, stbl.dataOffset, stbl.end);

        return {
            handler: parseHdlr(buf, hdlrInfo),
            mdhd: parseMdhd(buf, mdhdInfo),
            sampleEntry: parseSampleEntry(buf, stsdInfo),
            stsz: parseStsz(buf, stszInfo),
            stsc: parseStsc(buf, stscInfo),
            stco: stcoInfo ? parseStco(buf, stcoInfo, false) : (co64Info ? parseStco(buf, co64Info, true) : null),
            chunkOffsetIs64: !!co64Info,
            stts: parseStts(buf, sttsInfo),
            stss: stssInfo ? parseStco(buf, stssInfo, false) : null
        };
    }

    /**
     * Parse a reference file's moov. Returns {ftypBytes, mvhdTimescale, mvhdDuration, traks:[…]}.
     * traks contains parsed sample tables for each track.
     */
    async function parseReference(file) {
        var buf = await readFile(file);
        var ftypInfo = findBox(buf, FOURCC_FTYP, 0, buf.length);
        var moovInfo = findBox(buf, FOURCC_MOOV, 0, buf.length);
        if (!moovInfo) throw new Error('reference has no moov');

        var ftypBytes = ftypInfo ? buf.slice(ftypInfo.offset, ftypInfo.offset + ftypInfo.size) : null;
        var mvhdInfo = findBox(buf, FOURCC_MVHD, moovInfo.dataOffset, moovInfo.end);
        var mvhdTimescale = 1000;
        var mvhdDuration = 0;
        if (mvhdInfo) {
            var mp = mvhdInfo.dataOffset;
            var mver = buf[mp];
            if (mver === 1) {
                mvhdTimescale = readU32(buf, mp + 4 + 16);
                mvhdDuration = readU64(buf, mp + 4 + 20);
            } else {
                mvhdTimescale = readU32(buf, mp + 4 + 8);
                mvhdDuration = readU32(buf, mp + 4 + 12);
            }
        }

        var traks = [];
        eachTopBox(buf, moovInfo.dataOffset, moovInfo.end, function (b) {
            if (b.type === FOURCC_TRAK) {
                var t = parseTrak(buf, b);
                if (t) traks.push(t);
            }
        });

        return {
            ftypBytes: ftypBytes,
            mvhdTimescale: mvhdTimescale,
            mvhdDuration: mvhdDuration,
            traks: traks
        };
    }

    /**
     * Build the chunk-by-chunk layout of the REFERENCE file's mdat. Returns an array of
     * { trakIdx, refChunkOffset, sampleSizes:[], firstSampleIdx } sorted by refChunkOffset.
     * This is the "template" we apply to the broken file's mdat.
     *
     * For each trak, we expand stsc into per-chunk (samples_in_this_chunk) using stsz to
     * resolve actual sample sizes. The pattern matches what the original encoder wrote, so
     * applying it to the broken mdat (which was written by the same encoder before the kill)
     * produces a perfect sample table — no pattern matching, no NAL parsing required.
     */
    function buildRefChunkTemplate(traks) {
        var template = [];
        for (var ti = 0; ti < traks.length; ti++) {
            var t = traks[ti];
            if (!t.stsc || !t.stco || !t.stsz) continue;
            // Expand stsc: for each chunk index, resolve samples_per_chunk.
            var totalChunks = t.stco.length;
            var stscPattern = t.stsc;
            var sampleIdx = 0; // 0-based sample index across this trak
            for (var c = 0; c < totalChunks; c++) {
                var chunkNumber = c + 1; // 1-based
                var spc = stscPattern[stscPattern.length - 1].samplesPerChunk;
                for (var k = stscPattern.length - 1; k >= 0; k--) {
                    if (chunkNumber >= stscPattern[k].firstChunk) { spc = stscPattern[k].samplesPerChunk; break; }
                }
                var sizesForChunk = [];
                if (t.stsz.sampleSize > 0) {
                    // Constant-size: replicate.
                    for (var s = 0; s < spc && sampleIdx < t.stsz.sampleCount; s++) {
                        sizesForChunk.push(t.stsz.sampleSize);
                        sampleIdx++;
                    }
                } else {
                    for (var s2 = 0; s2 < spc && sampleIdx < t.stsz.sizes.length; s2++) {
                        sizesForChunk.push(t.stsz.sizes[sampleIdx]);
                        sampleIdx++;
                    }
                }
                if (sizesForChunk.length === 0) continue;
                template.push({
                    trakIdx: ti,
                    refChunkOffset: t.stco[c],
                    sampleSizes: sizesForChunk,
                    firstSampleIdx: sampleIdx - sizesForChunk.length
                });
            }
        }
        // Order by reference's mdat layout so we replay it the same way against broken's mdat.
        template.sort(function (a, b) { return a.refChunkOffset - b.refChunkOffset; });
        return template;
    }

    /**
     * Apply the reference's chunk template to the broken file's mdat. Returns per-trak
     * sample lists with broken-mdat offsets.
     *
     * @param {Object} mdat - { start, size } broken file's mdat data range
     * @param {Array} template - from buildRefChunkTemplate()
     * @param {number} trakCount
     * @returns {Object} { samplesByTrak: [trakIdx -> [{offset, size}]],
     *                     chunksByTrak: [trakIdx -> [{offset, sampleCount}]] }
     *   offsets are RELATIVE to mdat data start.
     */
    function applyTemplateToMdat(mdat, template, trakCount) {
        var samplesByTrak = [];
        var chunksByTrak = [];
        for (var i = 0; i < trakCount; i++) { samplesByTrak.push([]); chunksByTrak.push([]); }

        // Walk template chunks in order, consuming the broken mdat from offset 0.
        // This rebuilds the original interleaving: [video_chunk][audio_chunk][video_chunk]...
        // The trakIdx tells us which trak each chunk belongs to.
        var brokenPos = 0;
        for (var c = 0; c < template.length; c++) {
            var ent = template[c];
            var chunkSize = 0;
            for (var s = 0; s < ent.sampleSizes.length; s++) chunkSize += ent.sampleSizes[s];
            if (brokenPos + chunkSize > mdat.size) {
                // Truncation: try a partial chunk if we have at least one full sample worth of bytes.
                var partial = [];
                var consumed = 0;
                for (var s2 = 0; s2 < ent.sampleSizes.length; s2++) {
                    if (brokenPos + consumed + ent.sampleSizes[s2] > mdat.size) break;
                    partial.push({ offset: brokenPos + consumed, size: ent.sampleSizes[s2] });
                    consumed += ent.sampleSizes[s2];
                }
                if (partial.length === 0) break;
                chunksByTrak[ent.trakIdx].push({ offset: brokenPos, sampleCount: partial.length });
                for (var p = 0; p < partial.length; p++) samplesByTrak[ent.trakIdx].push(partial[p]);
                brokenPos += consumed;
                break;
            }
            chunksByTrak[ent.trakIdx].push({ offset: brokenPos, sampleCount: ent.sampleSizes.length });
            var chunkStart = brokenPos;
            for (var s3 = 0; s3 < ent.sampleSizes.length; s3++) {
                samplesByTrak[ent.trakIdx].push({ offset: brokenPos, size: ent.sampleSizes[s3] });
                brokenPos += ent.sampleSizes[s3];
            }
        }

        return { samplesByTrak: samplesByTrak, chunksByTrak: chunksByTrak };
    }

    /**
     * @deprecated Kept as a fallback for files where the reference template doesn't apply
     * (e.g. ref has no stsz, completely different camera). Scans NAL units for video and
     * ADTS sync words for audio. Less accurate than the template approach.
     *
     * Returns { videoSamples:[{offset,size}], audioSamples:[{offset,size}] }
     * Offsets are RELATIVE to mdat data start.
     */
    async function scanMdat(file, mdat, refTrak, opts) {
        opts = opts || {};
        var nalLengthSize = 4;
        var isHevc = false;
        var hasVideo = false, hasAudio = false;
        if (refTrak) {
            for (var t = 0; t < refTrak.traks.length; t++) {
                var rt = refTrak.traks[t];
                if (rt.handler === 'vide' && rt.sampleEntry) {
                    nalLengthSize = rt.sampleEntry.nalLengthSize || 4;
                    isHevc = rt.sampleEntry.codec === 'hevc';
                    hasVideo = true;
                }
                if (rt.handler === 'soun' && rt.sampleEntry) hasAudio = true;
            }
        } else {
            // Standalone mode (no reference): video-only, 4-byte AVCC prefix (Android
            // MediaRecorder default). Caller can override codec via opts.isHevc to retry
            // for iPhone-style HEVC content when an H.264 scan returns no samples.
            hasVideo = true;
            if (opts.isHevc) isHevc = true;
        }

        var CHUNK = 4 * 1024 * 1024; // 4 MB scan buffer
        var totalSize = mdat.size;
        var videoSamples = [];
        var audioSamples = [];

        /*
         * How much of the mdat we index, and how it is read (27.09.2026).
         *
         * Only the sample TABLE comes out of this scan — the output mdat is a Blob slice
         * of the original file and never passes through JS. So the scan never needed the
         * mdat in memory at once, only a window of it: pass 1 walks strictly forward, no
         * NAL it accepts is longer than 1 MB (probeNal), passes 2+ work on offsets, and
         * the few bytes later steps read (parameter sets, slice heads for the PPS check,
         * the first samples for parameter synthesis) are captured on the way.
         *
         * Until then the scan read `scanLimit` bytes in ONE piece — 200 MB on a
         * constrained tab, up to 1.5 GB on a 16 GB desktop — and both ends hurt:
         *   - Chrome answers a read it cannot hold with NotReadableError, the very error
         *     of a lost file (measured, Chrome 154: 2.5 GB file.arrayBuffer() →
         *     NotReadableError while a 4 KB slice of it reads fine). A 16 GB desktop
         *     with a 1.3 GB moov-missing recording (26.09) got «доступ к файлу пропал»
         *     six times while every streaming strategy read the file;
         *   - a phone indexed only the first 200 MB of its recording, so a 700 MB
         *     interrupted clip could never come back whole.
         * Now the whole mdat is indexed on any device, one 32 MB window at a time.
         * opts.scanLimit still caps it (tests), opts.windowBytes sets the window.
         */
        var WIN = (opts.windowBytes > 0) ? opts.windowBytes : 32 * 1024 * 1024;
        // A window must hold everything the walk READS of a NAL that starts before its
        // guard line: the length prefix, the two header bytes, the slice head (≤ 64 bytes),
        // a parameter set (≤ 1 KB). Not the NAL itself — a 4K keyframe is megabytes, and
        // the walk jumps over it by its length (27.09.2026).
        var GUARD = 64 * 1024 + 16;
        // The longest NAL we accept at all. Over BIG_NAL a NAL must also be a slice with a
        // plausible head: the only legitimately large NALs are pictures (a 4K IDR is 1–2 MB),
        // and a false one that long would swallow seconds of real frames.
        var MAX_NAL = 8 * 1024 * 1024, BIG_NAL = 256 * 1024;
        if (WIN < GUARD * 2) WIN = GUARD * 2;
        var scanBytes = (opts.scanLimit > 0) ? Math.min(totalSize, opts.scanLimit) : totalSize;
        var data = new Uint8Array(0), wb = 0, we = 0;      // current window: mdat-relative [wb, we)
        async function loadWindow(from) {
            var to = Math.min(scanBytes, from + WIN);
            data = new Uint8Array(await file.slice(mdat.start + from, mdat.start + to).arrayBuffer());
            wb = from; we = from + data.length;
        }
        await loadWindow(0);
        var len = scanBytes;                                 // absolute end of what we index
        function moreAfterWindow() { return we < scanBytes; }

        // Helper: read variable-length NAL prefix (p is mdat-relative, inside the window).
        function readNalLen(p) {
            var q = p - wb;
            if (nalLengthSize === 4) return readU32(data, q);
            if (nalLengthSize === 2) return (data[q] << 8) | data[q + 1];
            return data[q];
        }

        // Helper: validate an H.264/HEVC NAL unit by inspecting the first byte's nal_unit_type.
        // Restricted to types common cameras emit; forbidden_zero_bit must be 0 (cuts random
        // noise FP rate roughly in half).
        function isValidVideoNal(firstByte, isHevcMode) {
            if (firstByte & 0x80) return false; // forbidden_zero_bit
            if (isHevcMode) {
                var ht = (firstByte >> 1) & 0x3f;
                // Common HEVC types in camera output: VCL 0-9, IRAP 16-21, VPS 32, SPS 33, PPS 34, AUD 35, prefix-SEI 39, suffix-SEI 40.
                return (ht >= 0 && ht <= 9) || (ht >= 16 && ht <= 21) || ht === 32 || ht === 33 || ht === 34 || ht === 35 || ht === 39 || ht === 40;
            }
            var t = firstByte & 0x1f;
            // Common H.264 types: 1=non-IDR slice, 5=IDR, 6=SEI, 7=SPS, 8=PPS, 9=AUD.
            return t === 1 || t === 5 || t === 6 || t === 7 || t === 8 || t === 9;
        }

        // VCL classification: VCL slices are the picture data; non-VCL are SPS/PPS/SEI/AUD.
        // A new access unit starts when we see a non-VCL NAL after VCL ones (typical writer pattern)
        // OR when we encounter an AUD (H.264 type 9, HEVC type 35).
        function isVCL(firstByte, isHevcMode) {
            if (isHevcMode) {
                var ht = (firstByte >> 1) & 0x3f;
                // HEVC VCL: 0-9 (TRAIL_N..RASL_R), 16-21 (BLA/IDR/CRA)
                return (ht >= 0 && ht <= 9) || (ht >= 16 && ht <= 23);
            }
            var t = firstByte & 0x1f;
            // H.264 VCL: 1 (non-IDR slice), 5 (IDR), 19 (aux), 20 (slice ext)
            return t === 1 || t === 5 || t === 19 || t === 20;
        }
        function isAUD(firstByte, isHevcMode) {
            if (isHevcMode) return ((firstByte >> 1) & 0x3f) === 35;
            return (firstByte & 0x1f) === 9;
        }

        // Helper: probe a single NAL at position p. Returns {valid, len, firstByte}.
        // Per-NAL-type size caps reject false positives where a random 4-byte run "looks like"
        // a valid length prefix paired with a valid-looking NAL header byte. Real sizes:
        //   - SPS: typically 10-50 bytes, never > 8 KB
        //   - PPS: 5-30 bytes, never > 8 KB
        //   - SEI: variable, but rarely > 4 KB
        //   - AUD: 2 bytes, never > 16 bytes
        //   - VCL slice: hundreds of bytes to ~250 KB at 1080p, 1–2 MB for a 4K keyframe;
        //     over BIG_NAL only with a slice head that parses (see the end of this function)
        //   - Min 8 bytes — anything tinier is noise (SEI is rare to be <8B but tolerated)
        // The window always reaches GUARD past p, or to the end of what we index, so a
        // NAL is judged on the same bytes it would be in one piece.
        function probeNal(p) {
            if (p + nalLengthSize + 1 >= len) return { valid: false };
            var nlen = readNalLen(p);
            // Outer bounds: any NAL is at least 1 byte (the NAL header itself).
            if (nlen < 1 || nlen >= MAX_NAL) return { valid: false };
            if (p + nalLengthSize + nlen > len) return { valid: false };
            var fb = data[p - wb + nalLengthSize];
            if (!isValidVideoNal(fb, isHevc)) return { valid: false };
            if (isHevc) {
                // The HEVC NAL header is TWO bytes, and the second one is fixed for any
                // single-layer camera stream: nuh_layer_id = 0 (its top bit is the last bit
                // of byte one), nuh_temporal_id_plus1 ≠ 0. Checking only byte one let random
                // bytes through ~73× more often — false «slices» with a random length up to
                // 1 MB, each swallowing the real frames under it (27.09.2026: 39 NALs found
                // for 180 frames).
                if (nlen < 2) return { valid: false };
                var b1 = data[p - wb + nalLengthSize + 1];
                if ((fb & 1) || (b1 >> 3) || !(b1 & 7)) return { valid: false };
                var hh = (fb >> 1) & 0x3f;
                // TemporalId is 0 for VPS / SPS and for IRAP pictures (H.265 §7.4.2.2).
                if ((hh === 32 || hh === 33 || (hh >= 16 && hh <= 23)) && (b1 & 7) !== 1) return { valid: false };
                if (hh >= 32 && hh <= 34 && (nlen < 3 || nlen > 1024)) return { valid: false };
                if (hh === 35 && nlen > 16) return { valid: false };
                if ((hh === 39 || hh === 40) && nlen > 16384) return { valid: false };
                if ((hh <= 9 || (hh >= 16 && hh <= 21)) && nlen < 8) return { valid: false };
            }
            // Per-type tight caps for H.264.
            if (!isHevc) {
                var t = fb & 0x1f, nri = (fb >> 5) & 3;
                // nal_ref_idc is fixed by the standard for these (H.264 §7.4.1): never 0
                // for SPS / PPS / IDR, always 0 for SEI / AUD. A false «PPS» with nri 0 found
                // in the audio before a frame swallowed that frame's AUD and chained into its
                // slice — the picture came out behind a garbage parameter set (28.09.2026).
                if ((t === 7 || t === 8 || t === 5) && nri === 0) return { valid: false };
                if ((t === 6 || t === 9) && nri !== 0) return { valid: false };
                if (t === 6 && nlen > 8192) return { valid: false };
                if ((t === 7 || t === 8) && nlen > 1024) return { valid: false };
                if (t === 9 && (nlen < 1 || nlen > 16)) return { valid: false };
                if (t === 1 && nlen < 8) return { valid: false };
                if (t === 5 && nlen < 100) return { valid: false };
                if ((t === 7 || t === 8) && nlen < 2) return { valid: false };
            }
            // Big is only ever a picture. Until 27.09.2026 a P slice over 256 KB and any NAL
            // over 1 MB were refused outright, and pass 2 cut every access unit at 256 KB:
            // 1440p at 45 Mbps (keyframes of 293 KB) lost every keyframe and turned each one
            // into a garbage «SPS+PPS» sample — 5.6–64 % recall. Over 256 KB now: the first
            // slice of a picture (a looser «any slice head» let false megabyte «slices»
            // through often enough to lose 60 % of frames) that continues the stream's
            // frame_num, walked-on-to or found — a false «continuation» in the audio after a
            // frame swallows the frames behind it, and in ffmpeg's interleave no chain inside
            // can vouch for them. Over 1 MB: an intra picture, or the very next frame_num.
            // Measured on the DECODED file, ffmpeg's interleave, 29.09.2026 (frames of 570 /
            // of 225): «intra only» over 1 MB lost real noisy 4K P frames, which reach 2 MB
            // (2160p 217); no rule over 1 MB let false ones in (1440p 518); frame_num over
            // 256 KB as the only rule 530; this pair 550 and 225.
            if (nlen > BIG_NAL && !(isVCL(fb, isHevc) && sliceHeadParses(p, fb, nlen, true) && frameNumContinues(p, fb, nlen))) return { valid: false };
            if (nlen > 1024 * 1024 && !bigPictureFits(p, fb, nlen)) return { valid: false };
            return { valid: true, len: nlen, firstByte: fb };
        }

        /*
         * A NAL the walk only FOUND by search (not reached by walking on from the one
         * before it) is where every false positive comes from (27.09.2026). A random
         * 4-byte run passes probeNal about once in 180 KB — in the audio between video
         * chunks, and in the middle of a frame after the walk lost its step — and a false
         * «slice» carries a random length: the walk jumps over the real frames under it.
         * Measured on recordings with known sample positions: 64 % recall over three
         * sources × three layouts, 3 709 garbage samples (each one a garbage frame that
         * shifts every timestamp after it), HEVC 0–36 %.
         *
         * So a found NAL has to look like the START of something real:
         *   - a slice starts a picture: first_mb_in_slice = 0 / first_slice_segment_in_pic
         *     = 1 (a video chunk begins at a sample boundary, and a sample begins with its
         *     first slice), slice_type ≤ 9, an IDR is an I slice, the PPS id is one the
         *     stream has shown (or small, before any has been seen);
         *   - a parameter set / SEI / AUD is never alone: a chain that is only one of
         *     them is dropped (it used to be kept and swallow what followed it).
         */
        var knownPps = {}, knownPpsN = 0;
        function learnPps(n) {
            if (!n.body) return;
            var t = isHevc ? ((n.firstByte >> 1) & 0x3f) : (n.firstByte & 0x1f);
            if (isHevc ? t !== 34 : t !== 8) return;
            try {
                var br = new BitReader(removeEmulationBytes(n.body.subarray(isHevc ? 2 : 1, Math.min(n.body.length, 16))));
                var id = br.ue();
                if (id <= 63 && !knownPps[id]) { knownPps[id] = 1; knownPpsN++; }
            } catch (_) {}
        }
        // The head of a slice at p: first_mb_in_slice / first_slice_segment_in_pic_flag,
        // slice_type, PPS id and (H.264, once the stream's SPS is known) frame_num. Reads
        // ≤ 40 bytes, always inside the window (p is before the guard line). null = it
        // does not parse as a slice head at all.
        function sliceHead(p, fb, nlen) {
            var q = p - wb + nalLengthSize;
            var head = data.subarray(q + (isHevc ? 2 : 1), Math.min(q + nlen, q + 40));
            if (head.length < 1) return null;
            try {
                var br = new BitReader(removeEmulationBytes(head)), h = { first: false, st: -1, pps: -1, fn: -1 };
                if (isHevc) {
                    var ht = (fb >> 1) & 0x3f;
                    h.first = br.u(1) === 1;                     // first_slice_segment_in_pic_flag
                    if (ht >= 16 && ht <= 23) br.u(1);           // no_output_of_prior_pics_flag
                    h.pps = br.ue();
                    if (h.pps > 63) return null;
                } else {
                    var t = fb & 0x1f;
                    h.first = br.ue() === 0;                     // first_mb_in_slice
                    h.st = br.ue();
                    if (h.st > 9) return null;
                    if (t === 5 && ((h.st % 5) !== 2 && (h.st % 5) !== 4)) return null;   // IDR → I / SI
                    if (t === 5 && !((fb >> 5) & 3)) return null;                        // IDR is a reference
                    h.pps = br.ue();
                    if (h.pps > 255) return null;
                    if (fnBits) { if (sepColour) br.u(2); h.fn = br.u(fnBits); }
                }
                return h;
            } catch (_) { return null; }
        }
        function sliceHeadParses(p, fb, nlen, requireFirst) {
            var h = sliceHead(p, fb, nlen);
            if (!h) return false;
            if (requireFirst && !h.first) return false;
            return knownPpsN ? !!knownPps[h.pps] : h.pps <= 3;
        }
        /*
         * frame_num must continue the stream (H.264, 28.09.2026). A picture FOUND by
         * search after audio is the next picture of the stream, so its frame_num is the
         * last accepted one + 0…FN_SLACK (0: non-reference pictures share it; a few:
         * pictures the scan lost). An IDR restarts at 0. Garbage in the audio between
         * chunks carries a random value: on a 720p recording the 8 false «slices» that
         * got through every other rule each cost a garbage frame and decoder errors in
         * the file — and where the browser cannot judge the result (HEVC in most browsers,
         * a decode that times out) the repair rejects all of it for > 3 errors in its
         * first 5 s. After a long stretch with nothing accepted (> FN_STALE of the
         * file) the expectation is dropped, so a real gap cannot lock the scan out.
         */
        // Checked against the last FN_KEEP accepted pictures, not one: a false «slice»
        // that got through by chance must not become the reference every real picture
        // after it is judged by (measured: a 610-byte false IDR with frame_num 0 cost the
        // next 8 real frames). A found IDR is also never tiny: an intra picture costs more
        // than a typical predicted one (< ¼ of the median slice = the same false IDR).
        var fnBits = 0, sepColour = 0, fnHist = [], lastFnAt = -1;
        var FN_SLACK = 3, FN_KEEP = 3, FN_STALE = 4 * 1024 * 1024;
        function learnSps(n) {
            if (isHevc || fnBits || !n.body || (n.firstByte & 0x1f) !== 7) return;
            var sp = parseH264Sps(n.body);
            if (sp && sp.log2MaxFrameNum >= 4 && sp.log2MaxFrameNum <= 16) { fnBits = sp.log2MaxFrameNum; sepColour = sp.separateColourPlane ? 1 : 0; }
        }
        function frameNumContinues(p, fb, nlen) {
            if (isHevc || !fnBits || !fnHist.length || p - lastFnAt > FN_STALE) return true;
            var h = sliceHead(p, fb, nlen);
            if (!h || h.fn < 0) return true;
            if ((fb & 0x1f) === 5) return h.fn === 0 && !(recentSizes.length >= 8 && nlen * 4 < medianSliceSize());
            var max = 1 << fnBits;
            for (var i = 0; i < fnHist.length; i++) if (((h.fn - fnHist[i] + max) % max) <= FN_SLACK) return true;
            return false;
        }
        // Over 1 MB (see probeNal): an intra picture, or frame_num equal to the last
        // accepted one or one past it — the loose window above passes a random value 3–6
        // times in 16, this one 2 in 16, and a false megabyte «slice» costs seconds.
        function bigPictureFits(p, fb, nlen) {
            var h = sliceHead(p, fb, nlen);
            if (!h) return false;
            if (isHevc) { var hx = (fb >> 1) & 0x3f; return hx >= 16 && hx <= 21; }
            if ((fb & 0x1f) === 5 || (h.st % 5) === 2 || (h.st % 5) === 4) return true;
            if (!fnBits || !fnHist.length || h.fn < 0) return false;
            var max = 1 << fnBits, d0 = (h.fn - fnHist[fnHist.length - 1] + max) % max;
            return d0 <= 1;
        }
        function noteFrameNum(n) {
            if (!n.first || !(n.fn >= 0)) return;
            fnHist.push(n.fn);
            if (fnHist.length > FN_KEEP) fnHist.shift();
            lastFnAt = n.offset;
        }
        function startIsPlausible(p, pr) {
            if (!isVCL(pr.firstByte, isHevc)) return true;  // judged by the chain rule below
            return sliceHeadParses(p, pr.firstByte, pr.len, true) && frameNumContinues(p, pr.firstByte, pr.len);
        }
        // Is there a real chain (two NALs back to back, the first plausible as a start)
        // beginning INSIDE the span a lone candidate would swallow? Then the candidate is
        // the false one. Two in a row is what random bytes practically never produce —
        // but flat CAVLC pictures are full of 00 0x byte pairs, i.e. of NAL-looking
        // lengths, and a false 528 KB «slice» starting inside a real frame, with a
        // NAL-looking run where it happened to end, condemned the real frame around it
        // and then swallowed the next two (1440p, ffmpeg's interleave: 538 of 570 frames,
        // 29.09.2026). So the first NAL has to END inside the span, and a slice after a
        // slice has to keep frame_num or step it by one (an IDR restarts at 0), as every
        // real pair in decoding order does: 570 of 570, no decoder errors in the file
        // (the end rule alone: 555, 26 error lines).
        function chainStartsInside(from, to) {
            var spanEnd = to;
            to = Math.min(to, we - 64);                       // what this window holds
            for (var r = from + 1; r < to; r++) {
                // A length under 1 MB starts 0x00 0x0?: two byte reads turn away almost
                // every position before the full probe (this walk re-reads the whole span).
                if (nalLengthSize === 4) {
                    if (data[r - wb] !== 0) { var z0 = data.indexOf(0, r - wb + 1); if (z0 < 0) break; r = wb + z0 - 1; continue; }
                    if (data[r - wb + 1] > 0x0f) continue;
                }
                var a = probeNal(r);
                if (!a.valid || !startIsPlausible(r, a)) continue;
                var r2 = r + nalLengthSize + a.len;
                if (r2 > spanEnd) continue;
                if (r2 + nalLengthSize + 64 > we || r2 >= len) continue;
                var b = probeNal(r2);
                if (b.valid && (!isVCL(b.firstByte, isHevc) || (sliceHeadParses(r2, b.firstByte, b.len, false) && fnFollows(r, a, r2, b)))) return true;
            }
            return false;
        }
        // Slice b right after slice a: the same picture (same frame_num) or the next one
        // (+1 after a reference picture, the same after a non-reference one).
        function fnFollows(r, a, r2, b) {
            if (isHevc || !fnBits || !isVCL(a.firstByte, isHevc)) return true;
            var ha = sliceHead(r, a.firstByte, a.len), hb = sliceHead(r2, b.firstByte, b.len);
            if (!ha || !hb || ha.fn < 0 || hb.fn < 0) return true;
            if ((b.firstByte & 0x1f) === 5) return hb.fn === 0;
            var max = 1 << fnBits;
            return ((hb.fn - ha.fn + max) % max) <= 1;
        }

        // A big NAL found by SEARCH that dwarfs the stream's own frames (> 8× the median of
        // the slices accepted so far) and has a confirmed picture starting within its first
        // 2 KB is the false one. In ffmpeg/OBS files one audio packet sits before every
        // frame, and a false «slice» begun there — 300 KB among 24 KB frames — swallowed a
        // dozen of them: each of those frames is followed by audio, never by a NAL, so no
        // chain inside could vouch for them. The size gate keeps this off streams whose
        // frames ARE big (clean 4K: a false «start» at the head of every frame, confirmed
        // by the next one, cost half the frames when this ran on every big candidate).
        var NEAR = 2048, NEAR_GAP = 4096, recentSizes = [];
        function noteSliceSize(n) {
            if (!isVCL(n.firstByte, isHevc)) return;
            recentSizes.push(n.size);
            if (recentSizes.length > 64) recentSizes.shift();
        }
        function medianSliceSize() {
            var s2 = recentSizes.slice().sort(function (a, b) { return a - b; });
            return s2[s2.length >> 1] || 0;
        }
        function dwarfsTheStream(size) {
            if (recentSizes.length < 8) return false;
            return size > 8 * medianSliceSize();
        }
        function startsWithinGap(from, to) {
            for (var q = from; q < to; q++) {
                if (nalLengthSize === 4) {
                    if (data[q - wb] !== 0) { var z1 = data.indexOf(0, q - wb + 1); if (z1 < 0) break; q = wb + z1 - 1; continue; }
                    if (data[q - wb + 1] > 0x7f) continue;
                }
                var c = probeNal(q);
                if (c.valid && isVCL(c.firstByte, isHevc) && startIsPlausible(q, c)) return true;
            }
            return false;
        }
        function pictureStartsNear(from, end) {
            var to = Math.min(from + NEAR, end, we - 64);
            for (var r = from + 1; r < to; r++) {
                if (nalLengthSize === 4) {
                    if (data[r - wb] !== 0) { var z2 = data.indexOf(0, r - wb + 1); if (z2 < 0) break; r = wb + z2 - 1; continue; }
                    if (data[r - wb + 1] > 0x7f) continue;
                }
                var a = probeNal(r);
                if (!a.valid || !isVCL(a.firstByte, isHevc) || !startIsPlausible(r, a)) continue;
                var re = r + nalLengthSize + a.len;
                if (re > end || re + nalLengthSize + 64 > we) continue;
                var b = probeNal(re);
                if (b.valid && (!isVCL(b.firstByte, isHevc) || sliceHeadParses(re, b.firstByte, b.len, false))) return true;
                if (startsWithinGap(re, Math.min(re + NEAR_GAP, we - 64))) return true;
            }
            return false;
        }

        // What later steps read from an accepted NAL, taken while its head is in the window:
        // a parameter set's body (≤ 1 KB), a slice's PPS id and whether it opens its picture.
        // The slice bytes synthesizeParams needs are read from the file when (rarely) it runs.
        function captureNal(n) {
            var q = n.offset - wb, fb = n.firstByte;
            var bodyAt = q + nalLengthSize, bodyEnd = q + n.size;
            var ht = (fb >> 1) & 0x3f;
            var paramSet = isHevc ? (ht >= 32 && ht <= 34) : ((fb & 0x1f) === 7 || (fb & 0x1f) === 8);
            if (paramSet) { n.body = data.slice(bodyAt, bodyEnd); return; }
            if (!isVCL(fb, isHevc)) return;
            // The PPS a slice points at sits in its first bytes: parse a 64-byte head,
            // not the whole slice (emulation-byte removal walks what it is given).
            var head = data.subarray(bodyAt, Math.min(bodyEnd, bodyAt + 64));
            n.pps = isHevc ? parseHevcSlicePPSId(head, ht) : parseSlicePPSId(head);
            try {
                var br = new BitReader(removeEmulationBytes(head.subarray(isHevc ? 2 : 1, 24)));
                n.first = isHevc ? br.u(1) === 1 : br.ue() === 0;
            } catch (_) { n.first = true; }
            var sh = sliceHead(n.offset, fb, n.size - nalLengthSize);
            n.fn = sh ? sh.fn : -1;
        }

        // Pass 1: collect NAL boundaries via CHAIN validation.
        //
        // The mdat layout is [video_chunk_1][audio_chunk_1][video_chunk_2][audio_chunk_2]…
        // Inside a video chunk, NALs are contiguous (length-prefixed AVCC, no gaps).
        // Between chunks, audio bytes intervene — those bytes might LOOK like valid NAL
        // prefixes (random 4-byte values can pass single-NAL sanity checks), but they're not.
        //
        // Strategy: at each position, walk forward greedily collecting consecutive valid NALs.
        // Accept the chain ONLY if it has ≥ 2 NALs (real video chunks always contain at least
        // [SPS][PPS][slice] or similar). Single isolated "NAL" matches in audio data are rejected.
        //
        // Windowing: a chain stops where the next NAL could run past the window; the next
        // round slides the window to that point and the walk goes on. The NALs collected
        // are the same as in one piece — a chain split at a window edge only means two
        // runs of contiguous NALs, and everything after pass 1 reads offsets.
        var rawNals = [];
        var pos = 0;
        var lastEnd = -1;            // end of the last accepted NAL: walking on from it is not a search
        while (pos < len) {
            if (pos > we - GUARD && moreAfterWindow()) { await loadWindow(pos); continue; }
            if (hasVideo) {
                var chain = [];
                var p = pos;
                var found = pos !== lastEnd;
                // A length under MAX_NAL starts 0x00 0x00–0x7f: the search jumps from zero
                // byte to zero byte (native indexOf) instead of probing every position.
                if (found && nalLengthSize === 4 && data[pos - wb] !== 0) {
                    // native search for the next zero byte instead of one probe per byte
                    var zp = data.indexOf(0, pos - wb + 1);
                    pos = zp < 0 ? we : wb + zp;
                    continue;
                }
                if (found && nalLengthSize === 4 && data[pos - wb + 1] > 0x7f) { pos++; continue; }
                while (p < len) {
                    if (p > we - GUARD && moreAfterWindow()) break;
                    var pr = probeNal(p);
                    if (!pr.valid) break;
                    if (found && !chain.length && !startIsPlausible(p, pr)) break;
                    // Every slice, walked-on-to or found, must have a head that parses: a
                    // false one right after a chunk's last frame (audio bytes) used to be
                    // taken — and a non-first «slice» now joins the frame before it.
                    if ((!found || chain.length) && isVCL(pr.firstByte, isHevc) && !sliceHeadParses(p, pr.firstByte, pr.len, false)) break;
                    chain.push({ offset: p, size: nalLengthSize + pr.len, firstByte: pr.firstByte });
                    p += nalLengthSize + pr.len;
                    // Cap chain length to avoid runaway in pathological cases.
                    if (chain.length > 10000) break;
                }
                // A BIG NAL, found or walked on to, is false if a real chain begins inside
                // what it would swallow: a 4 MB keyframe holds only its own picture, a false
                // 4 MB «slice» holds a hundred real frames (27.09.2026: two such in a row
                // swallowed 337 frames of a 720p recording). The chain is cut before it.
                for (var bi = 0; bi < chain.length; bi++) {
                    var bn = chain[bi];
                    if (bn.size - nalLengthSize <= BIG_NAL) continue;
                    // A picture announced by the SPS/PPS/SEI/AUD right before it in the same
                    // run is confirmed by them; on flat content a false «chain» inside a real
                    // keyframe used to cut it (3 of 20 at 1440p).
                    if (bi > 0 && !isVCL(chain[bi - 1].firstByte, isHevc)) continue;
                    if (chainStartsInside(bn.offset, bn.offset + bn.size)) {
                        p = bn.offset;
                        chain.length = bi;
                        break;
                    }
                }
                if (found && chain.length && chain[0].size - nalLengthSize > BIG_NAL && dwarfsTheStream(chain[0].size) &&
                    pictureStartsNear(chain[0].offset, chain[0].offset + chain[0].size)) {
                    chain.length = 0;
                }
                if (found && chain.length === 1) {
                    // A lone parameter set / SEI / AUD is not the start of anything; a lone
                    // slice is dropped when a real chain begins inside what it would swallow.
                    if (!isVCL(chain[0].firstByte, isHevc) || chainStartsInside(pos, p)) chain.length = 0;
                }
                if (chain.length >= 1) {
                    for (var ci = 0; ci < chain.length; ci++) {
                        captureNal(chain[ci]);
                        noteSliceSize(chain[ci]);
                        if (chain.length > 1 || !found) { learnSps(chain[ci]); learnPps(chain[ci]); }
                        noteFrameNum(chain[ci]);
                        rawNals.push(chain[ci]);
                    }
                    pos = p;
                    lastEnd = p;
                    continue;
                }
            }
            // Audio scanning intentionally disabled here. ADTS sync detection competes with video
            // NAL detection: if at pos K the audio scanner matches first (because random video bytes
            // happened to look like ADTS), it consumes a multi-KB "audio frame" and skips past real
            // video NALs that lived in those bytes. Real MP4 audio is raw AAC anyway (no ADTS sync),
            // so the audio scan was always a best-effort heuristic. With s10 being video-only, drop it.
            pos++;
        }
        data = null;   // the window is not needed past pass 1

        // Pass 2: group consecutive NAL units into access units (= video samples).
        //
        // Access-unit boundary rules (covers Android/iPhone single-slice-per-picture writers):
        //   1. AUD NAL (H.264 type 9, HEVC type 35) starts a new AU explicitly.
        //   2. A VCL NAL after a VCL NAL starts a new AU (each slice = one picture, the standard
        //      Android baseline/main-profile pattern). Multi-slice pictures are uncommon in
        //      camera output; this heuristic is the one Untrunc uses.
        //   3. A non-VCL NAL after a VCL NAL closes the current AU (the non-VCL NAL belongs to
        //      the next AU as its parameter set / SEI prefix).
        //   4. Non-contiguity (offset gap from previous NAL — we resynced past garbage) closes the
        //      current AU.
        // Mark each rawNal with whether it belongs to a VCL-bearing chunk. A "chunk" is a
        // run of contiguous NALs (offset[i] == offset[i-1] + size[i-1]). Real video chunks
        // always contain at least one VCL slice; false-positive NALs found in audio-interleave
        // bytes don't, since random byte coincidences only ever produce isolated single-NAL
        // matches or short runs of non-VCLs. Tagging here so the AU walk and SPS/PPS collection
        // can both reject false positives uniformly.
        var nalChunkHasVCL = new Array(rawNals.length).fill(false);
        if (rawNals.length) {
            var chunkStartIdx = 0;
            var chunkHasVCL = isVCL(rawNals[0].firstByte, isHevc);
            for (var ck = 1; ck <= rawNals.length; ck++) {
                var inSameChunk = ck < rawNals.length && rawNals[ck].offset === rawNals[ck - 1].offset + rawNals[ck - 1].size;
                if (ck === rawNals.length || !inSameChunk) {
                    // close previous chunk
                    for (var cm = chunkStartIdx; cm < ck; cm++) nalChunkHasVCL[cm] = chunkHasVCL;
                    chunkStartIdx = ck;
                    chunkHasVCL = ck < rawNals.length ? isVCL(rawNals[ck].firstByte, isHevc) : false;
                } else if (isVCL(rawNals[ck].firstByte, isHevc)) {
                    chunkHasVCL = true;
                }
            }
        }

        if (rawNals.length) {
            var auStart = rawNals[0].offset;
            var auEnd   = rawNals[0].offset + rawNals[0].size;
            var hasVCLInAU = isVCL(rawNals[0].firstByte, isHevc);
            var auInValidChunk = nalChunkHasVCL[0];
            var auFirst = 0;       // index of the AU's first NAL: the sample carries its NAL range
            for (var ni = 1; ni < rawNals.length; ni++) {
                var nal = rawNals[ni];
                var nalIsVCL = isVCL(nal.firstByte, isHevc);
                var nonContig = nal.offset !== auEnd;
                // A slice opens a new picture only if it is its FIRST slice (first_mb_in_slice
                // = 0 / first_slice_segment_in_pic_flag). «Every slice is a picture» made each
                // slice of a multi-slice frame (hardware encoders) its own sample: 0 % of such
                // recordings came back (27.09.2026). No size cap either: the 256 KB one cut
                // every 1440p keyframe in two.
                // An AUD after a picture is the non-VCL case below; before one (SPS/PPS
                // written ahead of the AUD, as dump_extra and some cameras do) it belongs to
                // the same access unit — splitting there emitted «SPS+PPS» as a sample.
                var startsNewAU = nonContig
                    || (!nalIsVCL && hasVCLInAU)
                    || (nalIsVCL && hasVCLInAU && nal.first !== false);
                if (startsNewAU) {
                    var auSize = auEnd - auStart;
                    // Only emit AUs that lived inside a VCL-bearing chunk. A "VCL-less AU"
                    // (purely non-VCL false-positive NALs found inside audio-interleave bytes)
                    // would otherwise become a phantom video sample, splitting real frames in
                    // half and producing "non-existing PPS N" / "decode_slice_header error"
                    // cascades when the decoder reads garbage as parameter sets.
                    if (auSize >= 8 && auSize <= 64 * 1024 * 1024 && auInValidChunk && hasVCLInAU) {
                        videoSamples.push({ offset: auStart, size: auSize, nal0: auFirst, nals: ni - auFirst });
                    }
                    auFirst = ni;
                    auStart = nal.offset;
                    auEnd = nal.offset + nal.size;
                    hasVCLInAU = nalIsVCL;
                    auInValidChunk = nalChunkHasVCL[ni];
                } else {
                    auEnd = nal.offset + nal.size;
                    if (nalIsVCL) hasVCLInAU = true;
                }
            }
            // Final AU
            if (auEnd > auStart && auInValidChunk && hasVCLInAU) {
                videoSamples.push({ offset: auStart, size: auEnd - auStart, nal0: auFirst, nals: rawNals.length - auFirst });
            }
        }

        // Collect parameter-set NALs (SPS/PPS, plus VPS for HEVC) for downstream avcC/hvcC
        // synthesis. Same VCL-chunk filter as AU collection: SPS/PPS NALs found in non-VCL
        // chunks are byte-coincidences inside audio data, not real parameter sets.
        var spsList = [], ppsList = [], vpsList = [];
        for (var pi = 0; pi < rawNals.length; pi++) {
            if (!nalChunkHasVCL[pi]) continue;
            var rn = rawNals[pi];
            var fb = rn.firstByte;
            if (!rn.body) continue;      // captured in pass 1 for every parameter-set NAL
            if (isHevc) {
                var ht = (fb >> 1) & 0x3f;
                if (ht === 32 && vpsList.length < 4) vpsList.push(rn.body);
                else if (ht === 33 && spsList.length < 4) spsList.push(rn.body);
                else if (ht === 34 && ppsList.length < 4) ppsList.push(rn.body);
            } else {
                var t = fb & 0x1f;
                if (t === 7 && spsList.length < 4) spsList.push(rn.body);
                else if (t === 8 && ppsList.length < 4) ppsList.push(rn.body);
            }
        }

        return {
            videoSamples: videoSamples,
            audioSamples: audioSamples,
            inStreamSps: spsList,
            inStreamPps: ppsList,
            inStreamVps: vpsList,
            isHevc: isHevc,
            nalLengthSize: nalLengthSize,
            rawNals: rawNals,
            nalChunkHasVCL: nalChunkHasVCL,
            // No mdatData any more: the mdat is read a window at a time. What later steps
            // read from it rides on the NAL: .body (parameter sets), .pps (the PPS id of a
            // slice), .first (opens its picture); synthesizeParams reads the few slices it
            // needs back from the file (readNal).
            readNal: function (n, maxBytes) {
                var at = mdat.start + n.offset, end = at + (maxBytes ? Math.min(n.size, maxBytes) : n.size);
                return file.slice(at, end).arrayBuffer().then(function (b) { return new Uint8Array(b); });
            },
            // How much of the mdat this scan actually looked at. Callers MUST measure
            // their "did we recover enough" gates against THIS, not against mdat.size:
            // the two diverge exactly when the file is big, which is when the gate
            // decides whether a large recording is recoverable at all.
            scannedBytes: len,
            mdatBytes: totalSize
        };
    }

    /**
     * Group samples into chunks following the reference's stsc pattern, but capped at the
     * actual sample count we recovered. This is what makes the new moov play correctly:
     * sample_count × samples_per_chunk relationship is preserved from the camera's writer,
     * with chunk offsets pointing into the broken file's surviving mdat.
     */
    function buildChunks(samples, refStsc, mdatAbsStart) {
        var chunks = [];
        if (!samples.length) return { chunks: [], stsc: [] };

        // Default: 1 sample per chunk if no ref stsc.
        var pattern = refStsc && refStsc.length ? refStsc : [{ firstChunk: 1, samplesPerChunk: 1, sampleDescIndex: 1 }];

        var sampleIdx = 0;
        var chunkIdx = 1;
        var newStsc = [];
        var lastSPC = -1;

        while (sampleIdx < samples.length) {
            // Find which stsc entry this chunk belongs to.
            var spc = pattern[pattern.length - 1].samplesPerChunk;
            var sdi = pattern[pattern.length - 1].sampleDescIndex || 1;
            for (var k = pattern.length - 1; k >= 0; k--) {
                if (chunkIdx >= pattern[k].firstChunk) {
                    spc = pattern[k].samplesPerChunk;
                    sdi = pattern[k].sampleDescIndex || 1;
                    break;
                }
            }
            if (spc !== lastSPC) {
                newStsc.push({ firstChunk: chunkIdx, samplesPerChunk: spc, sampleDescIndex: sdi });
                lastSPC = spc;
            }

            // Take min(spc, remaining) samples for this chunk.
            var take = Math.min(spc, samples.length - sampleIdx);
            var firstSample = samples[sampleIdx];
            chunks.push({ offset: mdatAbsStart + firstSample.offset, sampleCount: take });
            sampleIdx += take;
            chunkIdx++;
        }

        return { chunks: chunks, stsc: newStsc };
    }

    // --- Box builders ---
    function buildBox(typeStr, payload) {
        var size = 8 + payload.length;
        var out = new Uint8Array(size);
        writeU32(out, 0, size);
        out[4] = typeStr.charCodeAt(0);
        out[5] = typeStr.charCodeAt(1);
        out[6] = typeStr.charCodeAt(2);
        out[7] = typeStr.charCodeAt(3);
        out.set(payload, 8);
        return out;
    }

    function buildLargeBox(typeStr, payload) {
        // 64-bit size variant. Used when payload won't fit in 32-bit size field.
        var size = 16 + payload.length;
        var out = new Uint8Array(size);
        writeU32(out, 0, 1); // size=1 signals 64-bit
        out[4] = typeStr.charCodeAt(0);
        out[5] = typeStr.charCodeAt(1);
        out[6] = typeStr.charCodeAt(2);
        out[7] = typeStr.charCodeAt(3);
        writeU64(out, 8, size);
        out.set(payload, 16);
        return out;
    }

    function concatBuffers(arrays) {
        var total = 0;
        for (var i = 0; i < arrays.length; i++) total += arrays[i].length;
        var out = new Uint8Array(total);
        var off = 0;
        for (var j = 0; j < arrays.length; j++) {
            out.set(arrays[j], off);
            off += arrays[j].length;
        }
        return out;
    }

    // Build stsz box from sample sizes array.
    function buildStsz(sizes) {
        var payload = new Uint8Array(12 + sizes.length * 4);
        // version+flags = 0
        writeU32(payload, 4, 0); // sample_size = 0 (per-sample)
        writeU32(payload, 8, sizes.length);
        for (var i = 0; i < sizes.length; i++) writeU32(payload, 12 + i * 4, sizes[i]);
        return buildBox('stsz', payload);
    }

    // Build stsc box from chunks pattern.
    function buildStsc(stsc) {
        var payload = new Uint8Array(8 + stsc.length * 12);
        writeU32(payload, 4, stsc.length);
        for (var i = 0; i < stsc.length; i++) {
            writeU32(payload, 8 + i * 12, stsc[i].firstChunk);
            writeU32(payload, 8 + i * 12 + 4, stsc[i].samplesPerChunk);
            writeU32(payload, 8 + i * 12 + 8, stsc[i].sampleDescIndex);
        }
        return buildBox('stsc', payload);
    }

    // Build co64 box (we always use 64-bit offsets to be safe with large mdats).
    function buildCo64(offsets) {
        var payload = new Uint8Array(8 + offsets.length * 8);
        writeU32(payload, 4, offsets.length);
        for (var i = 0; i < offsets.length; i++) writeU64(payload, 8 + i * 8, offsets[i]);
        return buildBox('co64', payload);
    }

    // Build stts box from time-to-sample entries.
    function buildStts(entries) {
        var payload = new Uint8Array(8 + entries.length * 8);
        writeU32(payload, 4, entries.length);
        for (var i = 0; i < entries.length; i++) {
            writeU32(payload, 8 + i * 8, entries[i].sampleCount);
            writeU32(payload, 8 + i * 8 + 4, entries[i].sampleDelta);
        }
        return buildBox('stts', payload);
    }

    function buildStss(syncSamples) {
        var payload = new Uint8Array(8 + syncSamples.length * 4);
        writeU32(payload, 4, syncSamples.length);
        for (var i = 0; i < syncSamples.length; i++) writeU32(payload, 8 + i * 4, syncSamples[i]);
        return buildBox('stss', payload);
    }

    /**
     * Build an avcC box from in-stream SPS/PPS NAL bodies (no length prefixes, no start codes).
     * Used when ref's avcC doesn't match broken's actual parameter sets.
     */
    function buildAvcC(spsList, ppsList, nalLengthSize) {
        if (!spsList.length || !ppsList.length) return null;
        var sps0 = spsList[0];
        // Profile/level live in SPS bytes 1..3 (after the NAL header).
        // SPS body layout: [forbidden_zero_bit + nal_ref_idc + nal_unit_type] is not in body
        // Wait — body here EXCLUDES the NAL header byte, since we sliced after the prefix.
        // Actually we sliced after nalLengthSize bytes, so body STARTS WITH the NAL header byte.
        // The header byte is at body[0]. Profile is at body[1], constraints at body[2], level at body[3].
        var profile = sps0[1];
        var profCompat = sps0[2];
        var level = sps0[3];

        var payload = [];
        payload.push(1); // configurationVersion
        payload.push(profile);
        payload.push(profCompat);
        payload.push(level);
        payload.push(0xfc | ((nalLengthSize - 1) & 0x03));
        payload.push(0xe0 | (spsList.length & 0x1f));
        for (var i = 0; i < spsList.length; i++) {
            // SPS body in avcC has the NAL header byte stripped? NO — avcC stores the full NAL unit
            // (header byte + RBSP). Our sliced body INCLUDES the header byte, so we use as-is.
            payload.push((spsList[i].length >> 8) & 0xff, spsList[i].length & 0xff);
            for (var j = 0; j < spsList[i].length; j++) payload.push(spsList[i][j]);
        }
        payload.push(ppsList.length & 0xff);
        for (var pi = 0; pi < ppsList.length; pi++) {
            payload.push((ppsList[pi].length >> 8) & 0xff, ppsList[pi].length & 0xff);
            for (var pj = 0; pj < ppsList[pi].length; pj++) payload.push(ppsList[pi][pj]);
        }
        return buildBox('avcC', new Uint8Array(payload));
    }

    /**
     * Build an hvcC box from in-stream VPS/SPS/PPS NAL bodies.
     * Layout is more complex than avcC; we synthesise minimal valid config.
     */
    function buildHvcC(vpsList, spsList, ppsList, nalLengthSize) {
        if (!spsList.length) return null;
        var sps0 = spsList[0];
        // SPS profile_tier_level lives at SPS body bytes 2..13 (after NAL header + sps_video_parameter_set_id+sps_max_sub_layers).
        // For minimal valid config we reuse those 12 bytes verbatim — decoders only validate format.
        var ptl = sps0.slice(2, 14);
        if (ptl.length < 12) return null;

        var bytes = [];
        bytes.push(1); // configurationVersion
        // general_profile_space + tier + profile_idc (1 byte) + profile_compatibility (4) + constraint (6) + level (1) = 12 bytes
        for (var b = 0; b < 12; b++) bytes.push(ptl[b]);
        // min_spatial_segmentation_idc (12 bits, set 0) + reserved
        bytes.push(0xf0); bytes.push(0);
        // parallelismType (8 bits, set 0)
        bytes.push(0xfc);
        // chromaFormat (8 bits, set yuv420 = 1)
        bytes.push(0xfd);
        // bitDepthLumaMinus8 + bitDepthChromaMinus8 (each 8 bits, set 0)
        bytes.push(0xf8); bytes.push(0xf8);
        // avgFrameRate (16 bits, set 0)
        bytes.push(0); bytes.push(0);
        // constantFrameRate (2 bits) + numTemporalLayers (3) + temporalIdNested (1) + lengthSizeMinusOne (2)
        bytes.push(((nalLengthSize - 1) & 0x03) | 0x0c);
        // numOfArrays
        var arrCount = 0;
        if (vpsList.length) arrCount++;
        if (spsList.length) arrCount++;
        if (ppsList.length) arrCount++;
        bytes.push(arrCount);
        function pushArray(nalType, list) {
            if (!list.length) return;
            // array_completeness=1 + reserved=0 + NAL_unit_type
            bytes.push(0x80 | (nalType & 0x3f));
            // numNalus
            bytes.push((list.length >> 8) & 0xff); bytes.push(list.length & 0xff);
            for (var i = 0; i < list.length; i++) {
                bytes.push((list[i].length >> 8) & 0xff, list[i].length & 0xff);
                for (var j = 0; j < list[i].length; j++) bytes.push(list[i][j]);
            }
        }
        pushArray(32, vpsList);
        pushArray(33, spsList);
        pushArray(34, ppsList);
        return buildBox('hvcC', new Uint8Array(bytes));
    }

    /**
     * Build a stsd box for a video trak with synthesised codec config from in-stream parameter sets.
     * Cloning ref's stsd verbatim doesn't work when broken's actual SPS/PPS differ from ref's
     * (e.g. different PPS pic_init_qp, different SPS rotation flag, different parameter set IDs).
     *
     * @param {Uint8Array} refStsdBytes — ref's stsd box bytes (full box including header)
     * @param {Uint8Array} newConfigBox — new avcC or hvcC box (full box)
     * @returns {Uint8Array} new stsd with the codec config replaced
     */
    function rewriteStsdConfig(refStsdBytes, newConfigBox) {
        // stsd payload: 8 hdr + 4 version_flags + 4 entry_count + entries
        // First entry: 4 size + 4 type + 6 reserved + 2 dataRefIdx + 70 visualSampleEntry + child boxes
        // We need to find avcC/hvcC inside the first entry and replace it.
        var p = 16; // skip stsd header (8) + version (4) + entry_count (4)
        if (p + 8 > refStsdBytes.length) return refStsdBytes;
        var entrySize = readU32(refStsdBytes, p);
        var entryType = String.fromCharCode(refStsdBytes[p + 4], refStsdBytes[p + 5], refStsdBytes[p + 6], refStsdBytes[p + 7]);
        if (entryType !== 'avc1' && entryType !== 'avc3' && entryType !== 'hvc1' && entryType !== 'hev1') return refStsdBytes;
        var entryEnd = p + entrySize;
        // Walk child boxes inside entry starting at p+8+78 (sample entry header).
        var childStart = p + 8 + 78;
        var configType = (entryType.indexOf('avc') === 0) ? 'avcC' : 'hvcC';
        var oldStart = -1, oldEnd = -1;
        var cp = childStart;
        while (cp + 8 <= entryEnd) {
            var cs = readU32(refStsdBytes, cp);
            var ct = String.fromCharCode(refStsdBytes[cp + 4], refStsdBytes[cp + 5], refStsdBytes[cp + 6], refStsdBytes[cp + 7]);
            if (cs < 8) break;
            if (ct === configType) { oldStart = cp; oldEnd = cp + cs; break; }
            cp += cs;
        }
        if (oldStart < 0) return refStsdBytes;

        // Splice: [0..oldStart) + newConfigBox + [oldEnd..end)
        var beforeLen = oldStart;
        var afterLen = refStsdBytes.length - oldEnd;
        var out = new Uint8Array(beforeLen + newConfigBox.length + afterLen);
        out.set(refStsdBytes.subarray(0, oldStart), 0);
        out.set(newConfigBox, oldStart);
        out.set(refStsdBytes.subarray(oldEnd), oldStart + newConfigBox.length);

        // Fix entry size (at offset p in original, now still at offset p in out).
        var newEntrySize = entrySize - (oldEnd - oldStart) + newConfigBox.length;
        writeU32(out, p, newEntrySize);
        // Fix outer stsd box size (at offset 0).
        writeU32(out, 0, out.length);
        return out;
    }

    /**
     * Take a raw box from ref and return its bytes (for cloning stsd, hdlr, mdhd, tkhd, mvhd…).
     */
    async function readBoxBytes(file, info) {
        return new Uint8Array(await file.slice(info.offset, info.offset + info.size).arrayBuffer());
    }

    /**
     * Build a fresh stbl child box list: stsd(clone), stts(rebuilt), stss(rebuilt or omitted),
     * stsc(rebuilt), stsz(rebuilt), co64(rebuilt). Returns concatenated stbl payload.
     */
    function buildStbl(stsdBytes, sttsBytes, stscBytes, stszBytes, co64Bytes, stssBytes) {
        var parts = [stsdBytes, sttsBytes];
        if (stssBytes) parts.push(stssBytes);
        parts.push(stscBytes, stszBytes, co64Bytes);
        var payload = concatBuffers(parts);
        return buildBox('stbl', payload);
    }

    /**
     * Build a moov box by wrapping the ref's mvhd + reconstructed traks.
     * For each ref trak, we replace its stbl with the rebuilt one but copy
     * everything else (tkhd, mdia/mdhd/hdlr, minf header boxes) verbatim.
     */
    function readMovieTimescale(buf, moovInfo) {
        var mv = findBox(buf, FOURCC_MVHD, moovInfo.dataOffset, moovInfo.end);
        if (!mv) return 0;
        var v = buf[mv.offset + 8];
        return readU32(buf, mv.offset + (v === 1 ? 28 : 20));
    }

    async function buildMoov(refFile, refBuf, refMoovInfo, refTraks, brokenSamples, scanResult) {
        // Locate mvhd raw bytes.
        var mvhdInfo = findBox(refBuf, FOURCC_MVHD, refMoovInfo.dataOffset, refMoovInfo.end);
        var mvhdBytes = mvhdInfo ? refBuf.slice(mvhdInfo.offset, mvhdInfo.offset + mvhdInfo.size) : null;
        if (!mvhdBytes) throw new Error('reference moov missing mvhd');

        var trakBytesList = [];
        var trakIdx = 0;
        var movieDurOut = 0;

        // Walk ref traks in order; rebuild each that we have samples for.
        var trakBoxes = [];
        eachTopBox(refBuf, refMoovInfo.dataOffset, refMoovInfo.end, function (b) {
            if (b.type === FOURCC_TRAK) trakBoxes.push(b);
        });

        for (var i = 0; i < trakBoxes.length; i++) {
            var trakInfo = trakBoxes[i];
            var trakDesc = refTraks[trakIdx];
            var trakIdxThis = trakIdx;
            trakIdx++;
            if (!trakDesc) continue;
            if (trakDesc.handler !== 'vide' && trakDesc.handler !== 'soun') continue;

            // Pull samples + chunks for this trak from the template-applied result, OR fall back
            // to per-trak lists from the NAL scan (video-only path).
            var samples, chunks;
            if (brokenSamples.byTrak) {
                samples = brokenSamples.byTrak.samplesByTrak[trakIdxThis] || [];
                chunks = brokenSamples.byTrak.chunksByTrak[trakIdxThis] || [];
            } else {
                samples = trakDesc.handler === 'vide' ? (brokenSamples.video || []) : (brokenSamples.audio || []);
                chunks = [];
            }
            // Skip traks we have no samples for — output will simply not contain that trak.
            // This is how the audio trak is dropped in the NAL-scan path.
            if (!samples.length) continue;

            var refStts = trakDesc.stts;
            var trackDurMedia = 0;   // what the tables below describe, in the track's own timescale

            // stsz: per-sample sizes from template.
            var sizes = samples.map(function (s) { return s.size; });
            var stszBytes = buildStsz(sizes);

            // stsc + co64: derived from template chunks (already in broken-mdat-relative offsets).
            // Convert relative offsets to absolute output-file offsets.
            var newStsc = [];
            var lastSPC = -1;
            for (var ck = 0; ck < chunks.length; ck++) {
                if (chunks[ck].sampleCount !== lastSPC) {
                    newStsc.push({ firstChunk: ck + 1, samplesPerChunk: chunks[ck].sampleCount, sampleDescIndex: 1 });
                    lastSPC = chunks[ck].sampleCount;
                }
            }
            // Fallback if template produced no chunks (shouldn't happen with samples present).
            if (!newStsc.length) newStsc = [{ firstChunk: 1, samplesPerChunk: 1, sampleDescIndex: 1 }];
            var stscBytes = buildStsc(newStsc);
            var absChunkOffsets = chunks.length
                ? chunks.map(function (c) { return c.offset + brokenSamples.mdatAbsStart; })
                : samples.map(function (s) { return s.offset + brokenSamples.mdatAbsStart; });
            var co64Bytes = buildCo64(absChunkOffsets);

            // stts: prorate ref's pattern to our actual sample count.
            var sttsBytes;
            if (refStts && refStts.length) {
                // Sum ref total samples to compute ratio.
                var refTotal = 0;
                for (var rj = 0; rj < refStts.length; rj++) refTotal += refStts[rj].sampleCount;
                if (refTotal === 0) refTotal = 1;
                var ratio = samples.length / refTotal;
                var rebuilt = [];
                var assigned = 0;
                for (var rk = 0; rk < refStts.length; rk++) {
                    var cnt = Math.round(refStts[rk].sampleCount * ratio);
                    if (cnt > 0) rebuilt.push({ sampleCount: cnt, sampleDelta: refStts[rk].sampleDelta });
                    assigned += cnt;
                }
                // Adjust last entry to make total == samples.length exactly.
                if (rebuilt.length) {
                    rebuilt[rebuilt.length - 1].sampleCount += (samples.length - assigned);
                    if (rebuilt[rebuilt.length - 1].sampleCount <= 0) rebuilt.pop();
                }
                if (!rebuilt.length) {
                    // Fallback: single entry with default delta (1024 for AAC, timescale/30 for video).
                    var defaultDelta = trakDesc.handler === 'soun' ? 1024 : Math.max(1, Math.floor((trakDesc.mdhd ? trakDesc.mdhd.timescale : 30000) / 30));
                    rebuilt.push({ sampleCount: samples.length, sampleDelta: defaultDelta });
                }
                sttsBytes = buildStts(rebuilt);
                for (var rd = 0; rd < rebuilt.length; rd++) trackDurMedia += rebuilt[rd].sampleCount * rebuilt[rd].sampleDelta;
            } else {
                var fallbackDelta = trakDesc.handler === 'soun' ? 1024 : 1000;
                sttsBytes = buildStts([{ sampleCount: samples.length, sampleDelta: fallbackDelta }]);
                trackDurMedia = samples.length * fallbackDelta;
            }

            // stss: prorate sync samples for video.
            var stssBytes = null;
            if (trakDesc.handler === 'vide' && trakDesc.stss && trakDesc.stss.length) {
                var refTotalForStss = 0;
                if (trakDesc.stsz && trakDesc.stsz.sampleCount) refTotalForStss = trakDesc.stsz.sampleCount;
                else for (var rs = 0; rs < refStts.length; rs++) refTotalForStss += refStts[rs].sampleCount;
                if (refTotalForStss > 0) {
                    var stssRatio = samples.length / refTotalForStss;
                    var newStss = [];
                    for (var ss = 0; ss < trakDesc.stss.length; ss++) {
                        var mapped = Math.max(1, Math.round(trakDesc.stss[ss] * stssRatio));
                        if (mapped <= samples.length && (newStss.length === 0 || newStss[newStss.length - 1] !== mapped)) {
                            newStss.push(mapped);
                        }
                    }
                    if (newStss.length) stssBytes = buildStss(newStss);
                }
            }

            // Read raw stsd, hdlr, mdhd, tkhd, minf-header bytes from ref.
            var mdiaInfo = findBox(refBuf, FOURCC_MDIA, trakInfo.dataOffset, trakInfo.end);
            var minfInfo = findBox(refBuf, FOURCC_MINF, mdiaInfo.dataOffset, mdiaInfo.end);
            var stblInfo = findBox(refBuf, FOURCC_STBL, minfInfo.dataOffset, minfInfo.end);
            var stsdRawInfo = findBox(refBuf, FOURCC_STSD, stblInfo.dataOffset, stblInfo.end);
            var stsdBytes = refBuf.slice(stsdRawInfo.offset, stsdRawInfo.offset + stsdRawInfo.size);

            // For video traks: replace ref's avcC/hvcC with one synthesised from broken's
            // own in-stream SPS/PPS (and VPS for HEVC). Slice headers in broken's NAL units
            // reference broken's parameter set IDs — using ref's avcC causes "non-existing PPS X"
            // errors when IDs differ. This is the in-stream parameter-set extraction technique.
            if (trakDesc.handler === 'vide' && scanResult) {
                var newCodecBox = scanResult.isHevc
                    ? buildHvcC(scanResult.inStreamVps || [], scanResult.inStreamSps || [], scanResult.inStreamPps || [], scanResult.nalLengthSize || 4)
                    : buildAvcC(scanResult.inStreamSps || [], scanResult.inStreamPps || [], scanResult.nalLengthSize || 4);
                if (newCodecBox) {
                    stsdBytes = rewriteStsdConfig(stsdBytes, newCodecBox);
                }
            }

            var stblBytes = buildStbl(stsdBytes, sttsBytes, stscBytes, stszBytes, co64Bytes, stssBytes);

            // Rebuild minf: copy all original child boxes EXCEPT stbl, then append our stbl.
            var minfChildren = [];
            eachTopBox(refBuf, minfInfo.dataOffset, minfInfo.end, function (b) {
                if (b.type !== FOURCC_STBL) {
                    minfChildren.push(refBuf.slice(b.offset, b.offset + b.size));
                }
            });
            minfChildren.push(stblBytes);
            var minfBytes = buildBox('minf', concatBuffers(minfChildren));

            // Rebuild mdia: copy all children except minf, append rebuilt minf.
            /* The durations are THIS recording's. mdhd, tkhd and the edit list used to be copied
             * from the reference verbatim — and the edit list says how long the track PLAYS: a
             * recording rebuilt against a shorter reference clip came out cut to the clip's length
             * (30.10.2026, a 20 s HERO6 recording with a 12 s reference: 365 of 600 frames, the
             * exact length of the reference). The edit list goes (no composition offsets are
             * written here, so the media starts at 0), mdhd/tkhd/mvhd get the rebuilt length. */
            var mediaTs = 0, movieTsRef = readMovieTimescale(refBuf, refMoovInfo);
            var mdiaChildren = [];
            eachTopBox(refBuf, mdiaInfo.dataOffset, mdiaInfo.end, function (b) {
                if (b.type === FOURCC_MDHD) {
                    var md = refBuf.slice(b.offset, b.offset + b.size);
                    var mv = md[8];
                    mediaTs = readU32(md, mv === 1 ? 28 : 20);
                    if (mv === 1) writeU64(md, 32, trackDurMedia); else writeU32(md, 24, Math.min(0xffffffff, trackDurMedia));
                    mdiaChildren.push(md);
                    return;
                }
                if (b.type !== FOURCC_MINF) {
                    mdiaChildren.push(refBuf.slice(b.offset, b.offset + b.size));
                }
            });
            var trackDurMovie = (mediaTs > 0 && movieTsRef > 0) ? Math.round(trackDurMedia * movieTsRef / mediaTs) : 0;
            if (trackDurMovie > movieDurOut) movieDurOut = trackDurMovie;
            mdiaChildren.push(minfBytes);
            var mdiaBytes = buildBox('mdia', concatBuffers(mdiaChildren));

            // Rebuild trak: copy all children except mdia, append rebuilt mdia.
            var trakChildren = [];
            eachTopBox(refBuf, trakInfo.dataOffset, trakInfo.end, function (b) {
                if (b.type === FOURCC_EDTS) return;
                if (b.type === FOURCC_TKHD && trackDurMovie > 0) {
                    var tk = refBuf.slice(b.offset, b.offset + b.size);
                    if (tk[8] === 1) writeU64(tk, 36, trackDurMovie); else writeU32(tk, 28, Math.min(0xffffffff, trackDurMovie));
                    trakChildren.push(tk);
                    return;
                }
                if (b.type !== FOURCC_MDIA) {
                    trakChildren.push(refBuf.slice(b.offset, b.offset + b.size));
                }
            });
            trakChildren.push(mdiaBytes);
            var trakBytes = buildBox('trak', concatBuffers(trakChildren));
            trakBytesList.push(trakBytes);
        }

        // Assemble moov: copy all children of original moov except trak boxes,
        // then append our rebuilt trak boxes.
        var moovChildren = [];
        eachTopBox(refBuf, refMoovInfo.dataOffset, refMoovInfo.end, function (b) {
            if (b.type === FOURCC_MVHD && movieDurOut > 0) {
                var mh = refBuf.slice(b.offset, b.offset + b.size);
                if (mh[8] === 1) writeU64(mh, 32, movieDurOut); else writeU32(mh, 24, Math.min(0xffffffff, movieDurOut));
                moovChildren.push(mh);
                return;
            }
            if (b.type !== FOURCC_TRAK) {
                moovChildren.push(refBuf.slice(b.offset, b.offset + b.size));
            }
        });
        for (var ti = 0; ti < trakBytesList.length; ti++) moovChildren.push(trakBytesList[ti]);

        return buildBox('moov', concatBuffers(moovChildren));
    }

    // === Standalone (no-reference) reconstruction ===
    //
    // The with-ref reconstruct() uses the reference file ONLY for the trak template:
    //   mdhd timescale, tkhd width/height, hdlr type, stts pattern, mvhd timescale.
    // All of these can be derived from the broken file alone:
    //   - width/height from in-stream SPS (parsed below)
    //   - timescale: industry-standard 90000 for video, 1000 for movie
    //   - stts: single entry with 30 fps default (sampleDelta = 3000 in 90000 ticks)
    //   - hdlr: 'vide' hardcoded (video-only path, same as ref-based s10)
    //
    // This is the "fileless" mode of Untrunc: produces a video-only MP4 that plays
    // forward. Audio recovery from raw-AAC mdat is intentionally out of scope (same
    // limitation as ref-based s10).

    function removeEmulationBytes(rbsp) {
        // H.264/HEVC encoders insert 0x03 between any 0x00 0x00 ≤0x03 sequence in RBSP
        // to prevent confusion with start codes. Strip them before bit-parsing the SPS.
        var out = [];
        for (var i = 0; i < rbsp.length; i++) {
            if (i + 2 < rbsp.length && rbsp[i] === 0 && rbsp[i + 1] === 0 && rbsp[i + 2] === 3) {
                out.push(0, 0);
                i += 2;
            } else {
                out.push(rbsp[i]);
            }
        }
        return new Uint8Array(out);
    }

    function BitReader(bytes) {
        this.bytes = bytes;
        this.bitPos = 0;
    }
    BitReader.prototype.u = function (n) {
        var val = 0;
        for (var i = 0; i < n; i++) {
            var bytePos = this.bitPos >> 3;
            if (bytePos >= this.bytes.length) { this.bitPos++; continue; }
            var bit = (this.bytes[bytePos] >> (7 - (this.bitPos & 7))) & 1;
            val = (val << 1) | bit;
            this.bitPos++;
        }
        return val;
    };
    BitReader.prototype.ue = function () {
        // Unsigned Exp-Golomb: count leading zero bits, then read that many bits + 1.
        var leadingZeros = 0;
        while (leadingZeros < 32) {
            if (this.u(1) === 1) break;
            leadingZeros++;
        }
        var suffix = leadingZeros > 0 ? this.u(leadingZeros) : 0;
        return (1 << leadingZeros) - 1 + suffix;
    };
    BitReader.prototype.se = function () {
        var v = this.ue();
        return (v & 1) ? ((v + 1) >> 1) : -(v >> 1);
    };

    /**
     * Parse H.264 SPS to extract width, height, profile, level. Returns null if parse fails.
     * Spec: ITU-T H.264 (08/2021) §7.3.2.1.1.
     * Input: SPS NAL body (full NAL including header byte).
     */
    function parseH264Sps(nalBody) {
        if (!nalBody || nalBody.length < 4) return null;
        try {
            var rbsp = removeEmulationBytes(nalBody.subarray(1)); // skip NAL header byte
            var br = new BitReader(rbsp);
            var profile_idc = br.u(8);
            br.u(8); // constraint_set_flags + reserved
            var level_idc = br.u(8);
            br.ue(); // seq_parameter_set_id

            var chroma_format_idc = 1, separate_colour_plane_flag = 0, bit_depth_luma = 8;
            if (profile_idc === 100 || profile_idc === 110 || profile_idc === 122 ||
                profile_idc === 244 || profile_idc === 44 || profile_idc === 83 ||
                profile_idc === 86 || profile_idc === 118 || profile_idc === 128 ||
                profile_idc === 138 || profile_idc === 139 || profile_idc === 134 ||
                profile_idc === 135) {
                chroma_format_idc = br.ue();
                if (chroma_format_idc === 3) separate_colour_plane_flag = br.u(1);
                bit_depth_luma = br.ue() + 8; // bit_depth_luma_minus8
                br.ue(); // bit_depth_chroma_minus8
                br.u(1); // qpprime_y_zero_transform_bypass_flag
                var seq_scaling_matrix_present_flag = br.u(1);
                if (seq_scaling_matrix_present_flag) {
                    var listCount = chroma_format_idc !== 3 ? 8 : 12;
                    for (var li = 0; li < listCount; li++) {
                        var seq_scaling_list_present_flag = br.u(1);
                        if (seq_scaling_list_present_flag) {
                            var sizeOfScalingList = li < 6 ? 16 : 64;
                            var lastScale = 8, nextScale = 8;
                            for (var sj = 0; sj < sizeOfScalingList; sj++) {
                                if (nextScale !== 0) {
                                    var delta_scale = br.se();
                                    nextScale = (lastScale + delta_scale + 256) % 256;
                                }
                                lastScale = nextScale === 0 ? lastScale : nextScale;
                            }
                        }
                    }
                }
            }
            var log2_max_frame_num = br.ue() + 4;
            var pic_order_cnt_type = br.ue(), log2_max_poc_lsb = 0;
            if (pic_order_cnt_type === 0) {
                log2_max_poc_lsb = br.ue() + 4; // log2_max_pic_order_cnt_lsb_minus4
            } else if (pic_order_cnt_type === 1) {
                br.u(1); // delta_pic_order_always_zero_flag
                br.se(); // offset_for_non_ref_pic
                br.se(); // offset_for_top_to_bottom_field
                var num_ref_frames_in_pic_order_cnt_cycle = br.ue();
                for (var rk = 0; rk < num_ref_frames_in_pic_order_cnt_cycle; rk++) br.se();
            }
            br.ue(); // max_num_ref_frames
            br.u(1); // gaps_in_frame_num_value_allowed_flag
            var pic_width_in_mbs_minus1 = br.ue();
            var pic_height_in_map_units_minus1 = br.ue();
            var frame_mbs_only_flag = br.u(1);
            if (!frame_mbs_only_flag) br.u(1); // mb_adaptive_frame_field_flag
            br.u(1); // direct_8x8_inference_flag
            var frame_cropping_flag = br.u(1);
            var fcl = 0, fcr = 0, fct = 0, fcb = 0;
            if (frame_cropping_flag) {
                fcl = br.ue();
                fcr = br.ue();
                fct = br.ue();
                fcb = br.ue();
            }

            var width = (pic_width_in_mbs_minus1 + 1) * 16;
            var height = (2 - frame_mbs_only_flag) * (pic_height_in_map_units_minus1 + 1) * 16;
            if (frame_cropping_flag) {
                var subWidthC, subHeightC;
                if (chroma_format_idc === 1) { subWidthC = 2; subHeightC = 2; }
                else if (chroma_format_idc === 2) { subWidthC = 2; subHeightC = 1; }
                else if (chroma_format_idc === 3) { subWidthC = 1; subHeightC = 1; }
                else { subWidthC = 1; subHeightC = 1; }
                width -= subWidthC * (fcl + fcr);
                height -= subHeightC * (2 - frame_mbs_only_flag) * (fct + fcb);
            }
            if (width < 16 || height < 16 || width > 8192 || height > 8192) return null;

            // Continue into VUI parameters to extract real frame rate via timing_info.
            // ITU-T H.264 Annex E. Phone encoders almost always set timing_info_present_flag,
            // so this lets standalone reconstruct produce the correct playback speed instead
            // of the 30 fps default fallback. If VUI parsing fails partway, return what we
            // have — fps stays 0 and caller uses fps=30.
            var fps = 0;
            try {
                var vui_parameters_present_flag = br.u(1);
                if (vui_parameters_present_flag) {
                    var aspect_ratio_info_present_flag = br.u(1);
                    if (aspect_ratio_info_present_flag) {
                        var aspect_ratio_idc = br.u(8);
                        if (aspect_ratio_idc === 255) { br.u(16); br.u(16); } // Extended_SAR
                    }
                    if (br.u(1)) br.u(1); // overscan_info_present + overscan_appropriate
                    if (br.u(1)) {
                        // video_signal_type
                        br.u(3); // video_format
                        br.u(1); // video_full_range_flag
                        if (br.u(1)) { // colour_description_present_flag
                            br.u(8); br.u(8); br.u(8); // primaries, transfer, matrix
                        }
                    }
                    if (br.u(1)) { // chroma_loc_info_present_flag
                        br.ue(); br.ue();
                    }
                    var timing_info_present_flag = br.u(1);
                    if (timing_info_present_flag) {
                        // num_units_in_tick / time_scale are u(32). BitReader.u tops at
                        // 32-bit, so for huge values we'd lose precision past 2^31. Phone
                        // values are small (num_units_in_tick: 1, time_scale: 60000 etc.)
                        // so 32-bit unsigned interpretation as JS number is safe.
                        var num_units_in_tick = br.u(32) >>> 0;
                        var time_scale = br.u(32) >>> 0;
                        if (num_units_in_tick > 0 && time_scale > 0) {
                            // Per ITU-T H.264 Annex E.2.1: real fps = time_scale / (2 * num_units_in_tick).
                            var computed = time_scale / (2 * num_units_in_tick);
                            if (computed > 1 && computed <= 300) fps = computed;
                        }
                    }
                }
            } catch (_) { /* VUI parse failed — fps stays 0, caller falls back to default */ }

            return { width: width, height: height, profile: profile_idc, level: level_idc, fps: fps,
                     log2MaxFrameNum: log2_max_frame_num, separateColourPlane: separate_colour_plane_flag,
                     // The fields a slice header needs to give up its picture order count
                     // (gopro-sos.js orders B-frames for the composition table with them).
                     pocType: pic_order_cnt_type, log2MaxPocLsb: log2_max_poc_lsb,
                     frameMbsOnly: frame_mbs_only_flag, chromaFormat: chroma_format_idc, bitDepth: bit_depth_luma };
        } catch (_) { return null; }
    }

    /**
     * Parse H.265/HEVC SPS to extract width/height/profile/level. Returns null on parse failure.
     * Spec: ITU-T H.265 (08/2021) §7.3.2.2.1.
     * Input: SPS NAL body (full NAL, INCLUDING 2-byte NAL header — HEVC has 2-byte headers
     * unlike H.264's 1-byte).
     *
     * Only parses enough to extract pic_width/height_in_luma_samples + conformance_window
     * cropping. profile_tier_level structure is walked but bytes are read, not interpreted
     * beyond extracting general_profile_idc and general_level_idc.
     */
    function parseH265Sps(nalBody) {
        if (!nalBody || nalBody.length < 16) return null;
        try {
            // HEVC NAL header is 2 bytes; SPS RBSP starts at byte 2.
            var rbsp = removeEmulationBytes(nalBody.subarray(2));
            var br = new BitReader(rbsp);

            br.u(4); // sps_video_parameter_set_id
            var sps_max_sub_layers_minus1 = br.u(3);
            br.u(1); // sps_temporal_id_nesting_flag

            // profile_tier_level structure
            br.u(2);  // general_profile_space
            br.u(1);  // general_tier_flag
            var general_profile_idc = br.u(5);
            // general_profile_compatibility_flag[32]
            for (var pf = 0; pf < 32; pf++) br.u(1);
            // general_progressive_source_flag, interlaced_source_flag,
            // non_packed_constraint_flag, frame_only_constraint_flag
            br.u(4);
            // 43 reserved/constraint bits + 1 reserved bit (= 44 bits total)
            for (var rc = 0; rc < 43; rc++) br.u(1);
            br.u(1); // general_inbld_flag or reserved_zero_bit
            var general_level_idc = br.u(8);

            // Per-sub-layer profile/level flags
            var subLayerProfilePresent = new Array(sps_max_sub_layers_minus1);
            var subLayerLevelPresent = new Array(sps_max_sub_layers_minus1);
            for (var sl = 0; sl < sps_max_sub_layers_minus1; sl++) {
                subLayerProfilePresent[sl] = br.u(1);
                subLayerLevelPresent[sl] = br.u(1);
            }
            // Reserved bits to align if max_sub_layers > 0
            if (sps_max_sub_layers_minus1 > 0) {
                for (var rsl = sps_max_sub_layers_minus1; rsl < 8; rsl++) br.u(2);
            }
            // Per-sub-layer profile_tier_level (each ~88 bits when present)
            for (var sl2 = 0; sl2 < sps_max_sub_layers_minus1; sl2++) {
                if (subLayerProfilePresent[sl2]) {
                    for (var sb = 0; sb < 88; sb++) br.u(1);
                }
                if (subLayerLevelPresent[sl2]) {
                    br.u(8);
                }
            }

            br.ue(); // sps_seq_parameter_set_id
            var chroma_format_idc = br.ue(), separate_colour_plane_flag = 0;
            if (chroma_format_idc === 3) separate_colour_plane_flag = br.u(1);

            var pic_width_in_luma_samples = br.ue();
            var pic_height_in_luma_samples = br.ue();
            var conformance_window_flag = br.u(1);
            var cwl = 0, cwr = 0, cwt = 0, cwb = 0;
            if (conformance_window_flag) {
                cwl = br.ue();
                cwr = br.ue();
                cwt = br.ue();
                cwb = br.ue();
            }
            // sub_width_c / sub_height_c per Table 6-1 of H.265:
            //   chroma_format_idc 1 (4:2:0) → 2/2; 2 (4:2:2) → 2/1; 3 (4:4:4) → 1/1; 0 (monochrome) → 1/1
            var subWidthC = 1, subHeightC = 1;
            if (chroma_format_idc === 1) { subWidthC = 2; subHeightC = 2; }
            else if (chroma_format_idc === 2) { subWidthC = 2; subHeightC = 1; }

            var width = pic_width_in_luma_samples - subWidthC * (cwl + cwr);
            var height = pic_height_in_luma_samples - subHeightC * (cwt + cwb);
            if (width < 16 || height < 16 || width > 8192 || height > 8192) return null;
            // What a slice header and an hvcC need beyond the picture size (gopro-sos.js):
            // bit depth and chroma for the hvcC, the POC width and the reorder depth for the
            // composition table. A failure past the size keeps the size.
            var more = {};
            try {
                more.bitDepthLuma = br.ue() + 8;
                more.bitDepthChroma = br.ue() + 8;
                more.log2MaxPocLsb = br.ue() + 4;
                var ordering = br.u(1), reorder = 0;
                for (var oi = ordering ? 0 : sps_max_sub_layers_minus1; oi <= sps_max_sub_layers_minus1; oi++) {
                    br.ue();                       // sps_max_dec_pic_buffering_minus1
                    reorder = br.ue();             // sps_max_num_reorder_pics
                    br.ue();                       // sps_max_latency_increase_plus1
                }
                more.maxNumReorder = reorder;
            } catch (_) {}
            return { width: width, height: height, profile: general_profile_idc, level: general_level_idc,
                     chromaFormat: chroma_format_idc, separateColourPlane: separate_colour_plane_flag,
                     bitDepthLuma: more.bitDepthLuma || 8, bitDepthChroma: more.bitDepthChroma || 8,
                     log2MaxPocLsb: more.log2MaxPocLsb || 0, maxNumReorder: more.maxNumReorder };
        } catch (_) { return null; }
    }

    // --- Synthetic box builders for standalone path ---

    function buildFtyp() {
        var payload = new Uint8Array([
            0x69, 0x73, 0x6f, 0x6d, // major: isom
            0, 0, 2, 0,             // minor
            0x69, 0x73, 0x6f, 0x6d, // compat: isom
            0x69, 0x73, 0x6f, 0x32, // compat: iso2
            0x61, 0x76, 0x63, 0x31, // compat: avc1
            0x6d, 0x70, 0x34, 0x31  // compat: mp41
        ]);
        return buildBox('ftyp', payload);
    }

    function buildMvhd(timescale, duration) {
        var payload = new Uint8Array(100);
        // version+flags = 0; creation/modification = 0
        writeU32(payload, 12, timescale);
        writeU32(payload, 16, duration);
        writeU32(payload, 20, 0x00010000); // rate (1.0)
        payload[24] = 0x01; payload[25] = 0x00; // volume (1.0)
        // matrix: identity
        writeU32(payload, 36, 0x00010000); // a
        writeU32(payload, 52, 0x00010000); // d
        writeU32(payload, 68, 0x40000000); // w
        writeU32(payload, 96, 2); // next_track_ID
        return buildBox('mvhd', payload);
    }

    function buildTkhd(trackId, duration, width, height) {
        var payload = new Uint8Array(84);
        // flags = 0x000007 (track_enabled | in_movie | in_preview)
        payload[0] = 0; payload[1] = 0; payload[2] = 0; payload[3] = 0x07;
        writeU32(payload, 12, trackId);
        writeU32(payload, 20, duration);
        // matrix identity
        writeU32(payload, 40, 0x00010000); // a
        writeU32(payload, 56, 0x00010000); // d
        writeU32(payload, 72, 0x40000000); // w
        // width / height — 16.16 fixed-point
        writeU32(payload, 76, (width & 0xffff) << 16);
        writeU32(payload, 80, (height & 0xffff) << 16);
        return buildBox('tkhd', payload);
    }

    function buildMdhd(timescale, duration) {
        var payload = new Uint8Array(24);
        writeU32(payload, 12, timescale);
        writeU32(payload, 16, duration);
        // language = 'und' (0x55C4)
        payload[20] = 0x55; payload[21] = 0xc4;
        return buildBox('mdhd', payload);
    }

    function buildHdlr(handlerType, name) {
        var nameBytes = new TextEncoder().encode(name || 'VideoHandler');
        var payload = new Uint8Array(24 + nameBytes.length + 1);
        payload[8]  = handlerType.charCodeAt(0);
        payload[9]  = handlerType.charCodeAt(1);
        payload[10] = handlerType.charCodeAt(2);
        payload[11] = handlerType.charCodeAt(3);
        payload.set(nameBytes, 24);
        return buildBox('hdlr', payload);
    }

    function buildVmhd() {
        // version+flags = 0x000001 (no_lean_ahead = 1, required by spec)
        var payload = new Uint8Array(12);
        payload[3] = 1;
        return buildBox('vmhd', payload);
    }

    function buildDinf() {
        // dref → url with self-reference flag = 0x000001
        var urlPayload = new Uint8Array(4);
        urlPayload[3] = 1; // flags: self-reference (data-is-in-this-file)
        var urlBox = buildBox('url ', urlPayload);
        var drefPayload = new Uint8Array(8 + urlBox.length);
        // version+flags = 0, entry_count = 1
        writeU32(drefPayload, 4, 1);
        drefPayload.set(urlBox, 8);
        var drefBox = buildBox('dref', drefPayload);
        return buildBox('dinf', drefBox);
    }

    function buildAvc1(width, height, avcCBox) {
        // Visual sample entry: 8 reserved+drIdx + 16 pre_defined + 2 width + 2 height
        // + 4 horizres + 4 vertres + 4 reserved + 2 frame_count + 32 compressorname
        // + 2 depth + 2 pre_defined = 78 bytes.
        var header = new Uint8Array(78);
        header[7] = 1; // dataReferenceIndex
        header[24] = (width >> 8) & 0xff; header[25] = width & 0xff;
        header[26] = (height >> 8) & 0xff; header[27] = height & 0xff;
        // horiz/vert resolution 72.0 (16.16) → 0x00480000
        header[28] = 0x00; header[29] = 0x48;
        header[32] = 0x00; header[33] = 0x48;
        header[40] = 0; header[41] = 1; // frame_count
        header[74] = 0x00; header[75] = 0x18; // depth = 24
        header[76] = 0xff; header[77] = 0xff; // pre_defined
        var payload = concatBuffers([header, avcCBox]);
        return buildBox('avc1', payload);
    }

    function buildStsdAvc1(avc1Box) {
        var hdr = new Uint8Array(8);
        writeU32(hdr, 4, 1); // entry_count
        return buildBox('stsd', concatBuffers([hdr, avc1Box]));
    }

    /**
     * Build hev1 sample entry (HEVC version of avc1). `hev1` tag means parameter sets MAY
     * appear in mdat; `hvc1` requires them in extradata only. Standalone reconstruction
     * keeps them in the bitstream (as Android MediaRecorder writes them) — hev1 is the
     * right tag.
     */
    function buildHev1(width, height, hvcCBox) {
        var header = new Uint8Array(78);
        header[7] = 1; // dataReferenceIndex
        header[24] = (width >> 8) & 0xff; header[25] = width & 0xff;
        header[26] = (height >> 8) & 0xff; header[27] = height & 0xff;
        header[28] = 0x00; header[29] = 0x48; // horiz res 72
        header[32] = 0x00; header[33] = 0x48; // vert res 72
        header[40] = 0; header[41] = 1; // frame_count
        header[74] = 0x00; header[75] = 0x18; // depth = 24
        header[76] = 0xff; header[77] = 0xff; // pre_defined
        return buildBox('hev1', concatBuffers([header, hvcCBox]));
    }

    function buildStsdHev1(hev1Box) {
        var hdr = new Uint8Array(8);
        writeU32(hdr, 4, 1);
        return buildBox('stsd', concatBuffers([hdr, hev1Box]));
    }

    /**
     * Parse H.264 PPS to extract pic_parameter_set_id. NAL header is 1 byte.
     * Used in standalone path to verify slice headers reference our extracted PPS —
     * slices that reference a different PPS id are false positives (audio bytes
     * that happen to look like a slice NAL).
     */
    function parsePPSId(ppsBody) {
        if (!ppsBody || ppsBody.length < 2) return -1;
        try {
            var rbsp = removeEmulationBytes(ppsBody.subarray(1));
            var br = new BitReader(rbsp);
            return br.ue();
        } catch (_) { return -1; }
    }

    /**
     * Parse HEVC PPS to extract pps_pic_parameter_set_id (0..63).
     * HEVC NAL header is 2 bytes; PPS RBSP starts at byte 2 with pps_pic_parameter_set_id (UE).
     */
    function parseHevcPPSId(ppsBody) {
        if (!ppsBody || ppsBody.length < 3) return -1;
        try {
            var rbsp = removeEmulationBytes(ppsBody.subarray(2));
            var br = new BitReader(rbsp);
            var ppsId = br.ue();
            if (ppsId > 63) return -1;
            return ppsId;
        } catch (_) { return -1; }
    }

    /**
     * Parse HEVC slice header's slice_pic_parameter_set_id. HEVC NAL header is 2 bytes;
     * slice header starts with first_slice_segment_in_pic_flag (1 bit), then for IRAP NALs
     * (nut 16..23) a no_output_of_prior_pics_flag bit, then pps_id (UE).
     */
    function parseHevcSlicePPSId(sliceBody, nut) {
        if (!sliceBody || sliceBody.length < 3) return -1;
        try {
            var rbsp = removeEmulationBytes(sliceBody.subarray(2));
            var br = new BitReader(rbsp);
            br.u(1); // first_slice_segment_in_pic_flag
            if (nut >= 16 && nut <= 23) br.u(1); // no_output_of_prior_pics_flag for IRAP
            var ppsId = br.ue();
            if (ppsId > 63) return -1; // HEVC PPS id max 63
            return ppsId;
        } catch (_) { return -1; }
    }

    /**
     * Parse a slice NAL's pic_parameter_set_id. Skips first_mb_in_slice and slice_type fields.
     * Returns -1 on parse failure or implausible values.
     */
    function parseSlicePPSId(sliceBody) {
        if (!sliceBody || sliceBody.length < 2) return -1;
        try {
            var rbsp = removeEmulationBytes(sliceBody.subarray(1));
            var br = new BitReader(rbsp);
            br.ue(); // first_mb_in_slice
            var sliceType = br.ue();
            if (sliceType > 9) return -1;
            var ppsId = br.ue();
            if (ppsId > 31) return -1;
            return ppsId;
        } catch (_) { return -1; }
    }

    /**
     * Last resort for a stream that carries no parameter sets: build one and have a real
     * decoder judge it. Returns {sps, pps, width, height, …} or null.
     *
     * Only H.264 is attempted. HEVC parameter sets carry a profile-tier-level structure
     * whose fields cannot be defaulted the way AVC's can, and guessing there would produce
     * a decoder-accepted-but-wrong picture — worse than an honest failure.
     */
    async function synthesizeParams(scanned, opts, report) {
        var Synth = global.H264ParamsSynth;
        if (!Synth || scanned.isHevc) return null;
        if (opts && opts.synthesizeParams === false) return null;

        var headers = [], samples = [];
        var nls = scanned.nalLengthSize || 4;
        for (var i = 0; i < scanned.rawNals.length && headers.length < 64; i++) {
            if (!scanned.nalChunkHasVCL[i]) continue;
            var rn = scanned.rawNals[i];
            var t = rn.firstByte & 0x1f;
            if (t !== 1 && t !== 5) continue;
            if (!scanned.readNal) break;
            // The validator wants whole AVCC samples: [length][NAL], as a track stores them;
            // probeSliceHeader reads the slice payload, which begins AFTER the NAL header
            // byte — its first 4 KB is enough (recover() scores 60). Read from the file now:
            // the scan no longer holds the mdat.
            var whole = samples.length < 12 && rn.size <= 8 * 1024 * 1024;
            var bytes = await scanned.readNal(rn, whole ? 0 : nls + 1 + 4096);
            headers.push({ body: bytes.subarray(nls + 1, Math.min(bytes.length, nls + 1 + 4096)), type: t });
            if (whole) samples.push(bytes);
        }
        if (headers.length < 5 || !samples.length) return null;

        // opts.makeValidator(samples) lets a test supply its own decoder; the browser gets
        // WebCodecs. Either way a decoder must exist — with no judge available we bail
        // rather than hand back an unverified guess.
        var validate = (opts && typeof opts.makeValidator === 'function')
            ? opts.makeValidator(samples)
            : Synth.makeWebCodecsValidator(samples);
        if (!validate) return null;

        report('synthesising_parameter_sets', 35);
        try {
            return await Synth.recover(headers, {
                validate: validate,
                // Depth 16, not 6: an 854x480 fixture recovered only once its timing
                // candidate — ranked below 6th by the header score — was reached. The
                // shortlist is cheap to widen because the judge is WebCodecs (single-digit
                // ms per candidate), so the whole search stays a couple of seconds against
                // a repair that otherwise returns nothing at all.
                timingDepth: (opts && opts.timingDepth) || 16,
                onProgress: function (done, total) {
                    report('synthesising_parameter_sets', 35 + Math.round(5 * done / Math.max(1, total)));
                },
            });
        } catch (_) {
            return null;
        }
    }

    /**
     * Standalone reconstruct: rebuild a moov-missing MP4 using only the broken file's
     * own bytes. Extracts in-stream SPS/PPS, parses width/height, synthesises a minimal
     * one-trak (video-only) moov.
     */
    async function reconstructStandalone(brokenFile, opts) {
        opts = opts || {};
        var report = function (phase, pct) {
            if (opts.onProgress) try { opts.onProgress(phase, pct); } catch (_) {}
        };
        // Default fps; will be replaced below with VUI-parsed value if SPS has timing_info.
        var fps = opts.fps && opts.fps > 0 ? opts.fps : 30;

        // Telemetry side-channel — callers in repair-video can read this after a null
        // return to record WHY the standalone bailed (HEVC, too-few-samples, no-SPS, etc.).
        global.Mp4Untrunc._lastStandaloneBail = null;
        global.Mp4Untrunc._lastMdatVia = null;
        // Cleared per run, or a synthesis from the PREVIOUS file would be reported against
        // this one — the tool lets a user try several files in one session.
        global.Mp4Untrunc._lastSynthesis = null;
        function bail(reason, extra) {
            global.Mp4Untrunc._lastStandaloneBail = Object.assign({ reason: reason }, extra || {});
            return null;
        }

        report('locating_mdat', 10);
        var mdat = await findMdatOrWhole(brokenFile);
        global.Mp4Untrunc._lastMdatVia = mdat.via;

        report('scanning_nals', 25);
        // Try H.264 first (default), fall back to HEVC if no SPS/PPS or samples found.
        // The two codecs use disjoint NAL header byte patterns (H.264 SPS = 0x67/0x47/0x27/
        // 0x07; HEVC SPS = 0x42/0x43) so a wrong-codec scan reliably yields nothing rather
        // than false positives — making sequential try-then-fallback safe.
        var scanned = await scanMdat(brokenFile, mdat, null, { windowBytes: opts.windowBytes });
        var codecTried = 'h264';
        if (!scanned.rawNals || !scanned.rawNals.length || !scanned.inStreamSps.length || !scanned.inStreamPps.length) {
            report('scanning_nals_hevc', 30);
            var scannedHevc = await scanMdat(brokenFile, mdat, null, { isHevc: true, windowBytes: opts.windowBytes });
            if (scannedHevc.rawNals && scannedHevc.rawNals.length && scannedHevc.inStreamSps.length && scannedHevc.inStreamPps.length) {
                scanned = scannedHevc;
                codecTried = 'hevc';
            }
        }
        if (!scanned.rawNals || !scanned.rawNals.length) {
            report('no_nals_recovered', 100);
            return bail('no_nals');
        }
        var synthesized = null;
        if (!scanned.inStreamSps.length || !scanned.inStreamPps.length) {
            // No parameter sets in the stream — the single largest reason this path used to
            // give up (317 sessions / 30 d). The slices are intact and decodable; what is
            // missing is the description of them, which died with the moov. Rather than
            // send the user away for a donor clip from the same camera — a request measured
            // to be answered by only 1 user in 5 — synthesise a parameter set and let a real
            // decoder confirm it. Nothing is returned unless the decoder produced frames, so
            // this can only turn a bail into a verified recovery, never a wrong result.
            synthesized = await synthesizeParams(scanned, opts, report);
            if (!synthesized) {
                report('no_in_stream_parameter_sets', 100);
                return bail('no_in_stream_params');
            }
            scanned.inStreamSps = [synthesized.sps];
            scanned.inStreamPps = [synthesized.pps];
            global.Mp4Untrunc._lastSynthesis = {
                width: synthesized.width, height: synthesized.height,
                candidatesTried: synthesized.candidatesTried,
                ratio: synthesized.score ? Math.round(synthesized.score.ratio * 100) : 0,
            };
        }
        var isHevc = scanned.isHevc;

        report('parsing_sps', 40);
        var spsInfo = isHevc ? parseH265Sps(scanned.inStreamSps[0]) : parseH264Sps(scanned.inStreamSps[0]);
        if (!spsInfo) {
            report('sps_parse_failed', 100);
            return bail('sps_parse_failed', { codec: codecTried });
        }
        // Adopt fps from SPS VUI timing_info if present (H.264 only — HEVC VUI walk
        // would require parsing many conditional pre-VUI fields. Phone HEVC is almost
        // always 30 fps anyway, the default works fine there). Only honour spsInfo.fps
        // if caller didn't override via opts.fps and the value is meaningful (>0).
        if (!opts.fps && spsInfo.fps && spsInfo.fps > 0) {
            fps = spsInfo.fps;
        }

        // Parse our PPS id to verify slice headers reference the right one. In files with
        // 1-sample-per-chunk interleaved video+audio (typical Android), audio bytes between
        // video samples occasionally pass the NAL probe (bytes happen to encode a valid
        // length prefix + a valid NAL header). Real slices reference our extracted PPS id
        // (typically 0). False-positive slices have random byte data that decodes to random
        // PPS ids — usually != our id. Filtering by ppsId match cleans the sample list.
        var ourPpsId = isHevc ? parseHevcPPSId(scanned.inStreamPps[0]) : parsePPSId(scanned.inStreamPps[0]);
        if (ourPpsId < 0) ourPpsId = 0; // fall back to 0 (most common) if PPS parse fails

        report('filtering_samples', 50);

        // VCL classifier matches what scanMdat uses internally — H.264: types 1/5/19/20,
        // HEVC: NAL unit types 0..9 (TRAIL/TSA/STSA/RADL/RASL) and 16..23 (BLA/IDR/CRA).
        function isVclByte(fb) {
            if (isHevc) {
                var ht = (fb >> 1) & 0x3f;
                return (ht >= 0 && ht <= 9) || (ht >= 16 && ht <= 23);
            }
            var t = fb & 0x1f;
            return t === 1 || t === 5 || t === 19 || t === 20;
        }

        // Samples are the scan's access units — pass 2 makes one picture one sample: its
        // slices, and the AUD / parameter sets in front of it (28.09.2026). This path used
        // to build its own list, «every slice that points at our PPS is a sample», so a
        // frame of four slices came out as four pictures (570 frames → 2280 on a
        // multi-slice recording) and nothing pass 2 knew about pictures reached the file.
        // The PPS check stays, per picture: kept if one of its slices points at a PPS the
        // stream carries.
        // Nothing before the first keyframe (H.264 IDR, HEVC IRAP) can be decoded — the
        // pictures it refers to are not in the file (a body cut from the middle, junk in
        // front): those samples only made decoder errors. The keyframes also become the
        // stss — without one every sample was a sync sample, and a seek landed on a P frame.
        var rawNals = scanned.rawNals;
        var nalLengthSize = scanned.nalLengthSize || 4;
        var ourPpsIds = {};
        (scanned.inStreamPps || []).forEach(function (pp) {
            var id = isHevc ? parseHevcPPSId(pp) : parsePPSId(pp);
            if (id >= 0) ourPpsIds[id] = 1;
        });
        if (!Object.keys(ourPpsIds).length) ourPpsIds[ourPpsId] = 1;
        function isKeyByte(fb) {
            if (isHevc) { var h = (fb >> 1) & 0x3f; return h >= 16 && h <= 21; }
            return (fb & 0x1f) === 5;
        }
        var pics = [], anyKey = false;
        for (var vi = 0; vi < scanned.videoSamples.length; vi++) {
            var vs = scanned.videoSamples[vi], ours = false, key = false;
            for (var k = vs.nal0; k < vs.nal0 + vs.nals; k++) {
                var rn = rawNals[k];
                if (!isVclByte(rn.firstByte)) continue;
                if (typeof rn.pps === 'number' && ourPpsIds[rn.pps]) ours = true;
                if (isKeyByte(rn.firstByte)) key = true;
            }
            if (!ours) continue;
            if (key) anyKey = true;
            pics.push({ offset: vs.offset, size: vs.size, key: key });
        }
        var verifiedSamples = [], syncSamples = [];
        for (var pi2 = 0; pi2 < pics.length; pi2++) {
            if (anyKey && !verifiedSamples.length && !pics[pi2].key) continue;
            if (pics[pi2].key) syncSamples.push(verifiedSamples.length + 1);
            verifiedSamples.push({ offset: pics[pi2].offset, size: pics[pi2].size });
        }
        var stssBytes = (syncSamples.length && syncSamples.length < verifiedSamples.length) ? buildStss(syncSamples) : null;

        if (verifiedSamples.length < 5) {
            report('insufficient_video_recovered', 100);
            return bail('too_few_samples', { codec: codecTried, samples: verifiedSamples.length });
        }
        var totalRecovered = 0;
        for (var sj = 0; sj < verifiedSamples.length; sj++) totalRecovered += verifiedSamples[sj].size;
        // Against the SCANNED span, for the same reason as the reference path: measured
        // against the whole mdat this gate becomes impossible once the file outgrows the
        // scan limit (at 5 %, anything past ~scanLimit/0.05).
        if (totalRecovered < (scanned.scannedBytes || mdat.size) * 0.05) {
            // Lower threshold than ref-based (which uses 0.20) because standalone counts
            // only the slice-NAL bytes; audio bytes that legitimately live in mdat between
            // samples are NOT counted here. 5% is enough to confirm we have real video data.
            report('insufficient_video_recovered', 100);
            return bail('too_few_bytes', { codec: codecTried, pct: Math.round(100 * totalRecovered / mdat.size) });
        }

        report('building_moov', 60);

        var sampleCount = verifiedSamples.length;
        var mdhdTimescale = 90000;
        var mvhdTimescale = 1000;
        var sampleDelta = Math.round(mdhdTimescale / fps);
        var trackDurMdhd = sampleCount * sampleDelta;
        var trackDurMvhd = Math.round(trackDurMdhd * mvhdTimescale / mdhdTimescale);

        var sizes = verifiedSamples.map(function (s) { return s.size; });
        var stszBytes = buildStsz(sizes);
        var stscBytes = buildStsc([{ firstChunk: 1, samplesPerChunk: 1, sampleDescIndex: 1 }]);
        var sttsBytes = buildStts([{ sampleCount: sampleCount, sampleDelta: sampleDelta }]);

        // Reuse first SPS/PPS (and VPS for HEVC) only — later occurrences are encoder
        // duplicates (or false positives, already filtered by the chunk-VCL gate).
        var stsdBytes;
        if (isHevc) {
            var hvcCBox = buildHvcC(
                scanned.inStreamVps && scanned.inStreamVps.length ? [scanned.inStreamVps[0]] : [],
                [scanned.inStreamSps[0]],
                [scanned.inStreamPps[0]],
                scanned.nalLengthSize || 4
            );
            if (!hvcCBox) return bail('hvcC_build_failed', { codec: 'hevc' });
            var hev1Box = buildHev1(spsInfo.width, spsInfo.height, hvcCBox);
            stsdBytes = buildStsdHev1(hev1Box);
        } else {
            var avcCBox = buildAvcC([scanned.inStreamSps[0]], [scanned.inStreamPps[0]], scanned.nalLengthSize || 4);
            if (!avcCBox) return bail('avcC_build_failed', { codec: 'h264' });
            var avc1Box = buildAvc1(spsInfo.width, spsInfo.height, avcCBox);
            stsdBytes = buildStsdAvc1(avc1Box);
        }

        var probeOffsets = verifiedSamples.map(function (s) { return s.offset; });
        var co64Probe = buildCo64(probeOffsets);
        var stblProbe = buildStbl(stsdBytes, sttsBytes, stscBytes, stszBytes, co64Probe, stssBytes);
        var vmhdBytes = buildVmhd();
        var dinfBytes = buildDinf();
        var minfProbe = buildBox('minf', concatBuffers([vmhdBytes, dinfBytes, stblProbe]));
        var mdhdBytes = buildMdhd(mdhdTimescale, trackDurMdhd);
        var hdlrBytes = buildHdlr('vide', 'VideoHandler');
        var mdiaProbe = buildBox('mdia', concatBuffers([mdhdBytes, hdlrBytes, minfProbe]));
        var tkhdBytes = buildTkhd(1, trackDurMvhd, spsInfo.width, spsInfo.height);
        var trakProbe = buildBox('trak', concatBuffers([tkhdBytes, mdiaProbe]));
        var mvhdBytes = buildMvhd(mvhdTimescale, trackDurMvhd);
        var moovProbe = buildBox('moov', concatBuffers([mvhdBytes, trakProbe]));

        var ftypBytes = buildFtyp();
        var mdatHeaderSize = 16; // 64-bit large-size header
        var mdatAbsStart = ftypBytes.length + moovProbe.length + mdatHeaderSize;

        // Rebuild with REAL co64 offsets pointing into output mdat.
        var realOffsets = verifiedSamples.map(function (s) { return s.offset + mdatAbsStart; });
        var co64Real = buildCo64(realOffsets);
        var stblReal = buildStbl(stsdBytes, sttsBytes, stscBytes, stszBytes, co64Real, stssBytes);
        var minfReal = buildBox('minf', concatBuffers([vmhdBytes, dinfBytes, stblReal]));
        var mdiaReal = buildBox('mdia', concatBuffers([mdhdBytes, hdlrBytes, minfReal]));
        var trakReal = buildBox('trak', concatBuffers([tkhdBytes, mdiaReal]));
        var moovReal = buildBox('moov', concatBuffers([mvhdBytes, trakReal]));

        // co64 is fixed-width per entry, so probe and real moovs are identical size.
        if (moovReal.length !== moovProbe.length) {
            mdatAbsStart = ftypBytes.length + moovReal.length + mdatHeaderSize;
            realOffsets = verifiedSamples.map(function (s) { return s.offset + mdatAbsStart; });
            co64Real = buildCo64(realOffsets);
            stblReal = buildStbl(stsdBytes, sttsBytes, stscBytes, stszBytes, co64Real, stssBytes);
            minfReal = buildBox('minf', concatBuffers([vmhdBytes, dinfBytes, stblReal]));
            mdiaReal = buildBox('mdia', concatBuffers([mdhdBytes, hdlrBytes, minfReal]));
            trakReal = buildBox('trak', concatBuffers([tkhdBytes, mdiaReal]));
            moovReal = buildBox('moov', concatBuffers([mvhdBytes, trakReal]));
        }

        report('assembling_output', 85);

        var brokenMdatSlice = brokenFile.slice(mdat.start, mdat.start + mdat.size);

        var mdatHeader = new Uint8Array(16);
        writeU32(mdatHeader, 0, 1); // size=1 → 64-bit
        mdatHeader[4] = 0x6d; mdatHeader[5] = 0x64; mdatHeader[6] = 0x61; mdatHeader[7] = 0x74;
        writeU64(mdatHeader, 8, mdat.size + 16);

        report('finalizing', 95);
        var outBlob = new Blob([ftypBytes, moovReal, mdatHeader, brokenMdatSlice], { type: 'video/mp4' });
        // Surface the codec used so caller can record it on success telemetry.
        global.Mp4Untrunc._lastStandaloneCodec = codecTried;
        report('done', 100);
        return outBlob;
    }

    /**
     * Public entry point. Reconstructs a broken moov-missing MP4 using a working reference.
     *
     * @param {File} brokenFile
     * @param {File} referenceFile
     * @param {Object} [opts]
     * @param {Function} [opts.onProgress] phase-and-pct callback
     * @returns {Promise<Blob|null>} reconstructed MP4 (plays directly), or null on failure.
     */
    async function reconstruct(brokenFile, referenceFile, opts) {
        opts = opts || {};
        var report = function (phase, pct) {
            if (opts.onProgress) {
                try { opts.onProgress(phase, pct); } catch (_) {}
            }
        };

        report('reading_reference', 5);
        var refBuf = await readFile(referenceFile);
        var refMoovInfo = findBox(refBuf, FOURCC_MOOV, 0, refBuf.length);
        if (!refMoovInfo) return null;
        var ftypInfo = findBox(refBuf, FOURCC_FTYP, 0, refBuf.length);

        report('parsing_reference_traks', 15);
        var traks = [];
        eachTopBox(refBuf, refMoovInfo.dataOffset, refMoovInfo.end, function (b) {
            if (b.type === FOURCC_TRAK) {
                var t = parseTrak(refBuf, b);
                if (t) traks.push(t);
            }
        });
        if (!traks.length) return null;

        report('locating_mdat', 25);
        var mdat = await findMdatOrWhole(brokenFile);
        global.Mp4Untrunc._lastMdatVia = mdat.via;

        report('scanning_nals', 35);
        // (Audio scanning intentionally disabled — see scanMdat comments. s10 is video-only.)
        // NAL-scan recovery: parse the broken mdat for valid NAL units (validated by length
        // prefix sanity + valid NAL type), group them into access units (= video samples) via
        // VCL/AUD rules, ignore everything else (audio bytes, garbage).
        //
        // Why not template-based: ref's stsz sample sizes only match broken's when the two
        // recordings have identical frame content (i.e., when broken is literally a copy of
        // ref with the moov stripped). For two different recordings on the same camera, frame
        // sizes differ — applying ref's stsz blindly produces misaligned NAL boundaries and
        // a cascade of "Invalid NAL unit size" decoder errors.
        //
        // Audio recovery from raw-AAC mdat is intentionally out of scope: AAC samples in MP4
        // have no sync words, so without ref's exact stsz pattern matching (which itself only
        // works when broken == ref-without-moov) we can't find boundaries reliably. This s10
        // path produces a video-only output. It's still the first strategy that recovers PLAYABLE
        // video from arbitrary moov-missing files (without dropping back to FFmpeg re-encode).
        var scanned = await scanMdat(brokenFile, mdat, { traks: traks }, { windowBytes: opts.windowBytes });
        if (!scanned.videoSamples.length) {
            report('no_video_recovered', 100);
            return null;
        }
        // Safety gate: if too few samples or too few bytes recovered, the broken file probably
        // doesn't follow the assumed AVCC + in-stream SPS/PPS pattern (e.g. fragmented MP4,
        // proprietary container, garbage data). Bail out so the caller can fall through to the
        // existing FFmpeg-based strategies (s9 etc.) instead of producing a likely-bad output.
        var totalRecovered = 0;
        for (var sj = 0; sj < scanned.videoSamples.length; sj++) totalRecovered += scanned.videoSamples[sj].size;
        /*
         * Measure the yield against what the scan LOOKED AT, not against the whole mdat.
         *
         * These were the same number only while every file fit inside the scan limit. Once
         * a recording is larger, `totalRecovered` is bounded by the scanned span while the
         * threshold kept growing with the file — so above mdat.size ≈ scanLimit / 0.20 the
         * gate became arithmetically impossible to pass, and a perfectly recoverable
         * recording with a perfectly good reference returned null. Measured over 60 days
         * before this fix: 0 reference-based recoveries out of 10 attempts above 1 GB,
         * against 137 and 24 below it. Bug report #1556 spent 80 minutes on a 1391 MB .mov,
         * found the reference panel, uploaded a valid sample, and was told "unrepairable".
         */
        var scannedSpan = scanned.scannedBytes || mdat.size;
        if (scanned.videoSamples.length < 5 || totalRecovered < scannedSpan * 0.20) {
            report('insufficient_video_recovered', 100);
            return null;
        }
        report('reconstructing_moov', 70);
        var brokenSamples = {
            byTrak: null,
            video: scanned.videoSamples,
            audio: [], // intentionally empty — see comment above
            mdatAbsStart: 0
        };

        // Build moov with placeholder offsets so we can measure its size.
        var moovBytesProbe = await buildMoov(referenceFile, refBuf, refMoovInfo, traks, brokenSamples, scanned);

        // Now we know exact mdat absolute start in output: ftyp_size + moov_size + 8 (mdat header)
        var ftypSize = ftypInfo ? ftypInfo.size : 32;
        var mdatHeaderSize = 16; // 64-bit header (size=1 + type + 8-byte large size)
        var mdatAbsStartOut = ftypSize + moovBytesProbe.length + mdatHeaderSize;

        // Rebuild moov with corrected offsets.
        brokenSamples.mdatAbsStart = mdatAbsStartOut;
        var moovBytes = await buildMoov(referenceFile, refBuf, refMoovInfo, traks, brokenSamples, scanned);

        // If moov size changed slightly, fix offsets again. In practice the size is identical
        // (same number of entries), but we guard anyway.
        if (moovBytes.length !== moovBytesProbe.length) {
            mdatAbsStartOut = ftypSize + moovBytes.length + mdatHeaderSize;
            brokenSamples.mdatAbsStart = mdatAbsStartOut;
            moovBytes = await buildMoov(referenceFile, refBuf, refMoovInfo, traks, brokenSamples);
        }

        report('assembling_output', 85);

        // ftyp: clone from ref or synthesise minimal isom.
        var ftypBytes;
        if (ftypInfo) {
            ftypBytes = refBuf.slice(ftypInfo.offset, ftypInfo.offset + ftypInfo.size);
        } else {
            var ftypPayload = new Uint8Array([
                0x69, 0x73, 0x6f, 0x6d, // major: isom
                0, 0, 2, 0,             // minor
                0x69, 0x73, 0x6f, 0x6d, // compat: isom
                0x6d, 0x70, 0x34, 0x32  // compat: mp42
            ]);
            ftypBytes = buildBox('ftyp', ftypPayload);
        }

        // mdat: copy original mdat bytes from broken file as a single Blob slice (no full copy in JS).
        var brokenMdatSlice = brokenFile.slice(mdat.start, mdat.start + mdat.size);

        // mdat header: 64-bit size variant.
        var mdatHeader = new Uint8Array(16);
        writeU32(mdatHeader, 0, 1); // size=1 → 64-bit
        mdatHeader[4] = 0x6d; mdatHeader[5] = 0x64; mdatHeader[6] = 0x61; mdatHeader[7] = 0x74; // 'mdat'
        writeU64(mdatHeader, 8, mdat.size + 16);

        report('finalizing', 95);
        var outBlob = new Blob([ftypBytes, moovBytes, mdatHeader, brokenMdatSlice], { type: 'video/mp4' });
        report('done', 100);
        return outBlob;
    }

    /**
     * Quick check: does this reference file have a parseable moov + at least one A/V trak?
     */
    async function canReconstruct(referenceFile) {
        try {
            var refBuf = await readFile(referenceFile, 50 * 1024 * 1024);
            var moov = findBox(refBuf, FOURCC_MOOV, 0, refBuf.length);
            if (!moov) return false;
            var traks = [];
            eachTopBox(refBuf, moov.dataOffset, moov.end, function (b) {
                if (b.type === FOURCC_TRAK) traks.push(b);
            });
            return traks.length > 0;
        } catch (e) {
            return false;
        }
    }

    global.Mp4Untrunc = {
        reconstruct: reconstruct,
        reconstructStandalone: reconstructStandalone,
        canReconstruct: canReconstruct,
        // Internals exposed for testing.
        _internals: {
            findMdat: findMdat,
            findBox: findBox,
            findPath: findPath,
            parseReference: parseReference,
            scanMdat: scanMdat,
            parseH264Sps: parseH264Sps,
            parseH265Sps: parseH265Sps,
            // shared with gopro-sos.js (one owner for SPS parsing and the avcC box)
            removeEmulationBytes: removeEmulationBytes,
            buildAvcC: buildAvcC,
            buildBox: buildBox
        }
    };
})(typeof window !== 'undefined' ? window : this);
